import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { usePlatformStore } from '@/lib/store';
import { Spinner } from '@/components/shared/UIComponents';
import { cn } from '@/utils/helpers';
import type { BrandingConfig, FeatureConfig } from '@/types';
import { fetchPlanFeatureDefaults } from '@/lib/api';
import BrandingAssistant from '@/components/BrandingAssistant';
import { ShowcaseDestinationsTab } from '@/components/branding/ShowcaseDestinationsTab';
import { LogoUploader } from '@/components/branding/LogoUploader';
import { LogoLibraryPicker } from '@/components/branding/LogoLibraryPicker';
import {
  Brain, Wallet, Globe as GlobeIcon, Users, Heart, Mic, Package, Camera,
  FileText, BarChart3, WifiOff, Sparkles as SparklesLucide, MapPin as MapPinLucide,
  Video as VideoLucide, Eye, ChevronDown, Search, X as XIcon, GripVertical,
  // Icons for custom info card picker
  Star, Coffee, Sun, Moon, Music, BookOpen, Flag, Shield, Clock, Calendar,
  Phone, Mail, Home, Key, Compass, Lightbulb, MessageCircle, ThumbsUp, Award,
  UtensilsCrossed, Plane, Mountain, TreePine, Landmark, Handshake, Check,
  Navigation, Radio, CreditCard, PieChart, Gem,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const BRANDING_TABS = [
  { id: 'design', label: 'Brand & Design' },
  { id: 'splash', label: 'Splash Page' },
  { id: 'destinations', label: 'Destinations' },
  { id: 'cards', label: 'Cards' },
  { id: 'smart-planning', label: 'Smart Planning' },
  { id: 'emails', label: 'Emails & Social' },
  { id: 'seo', label: 'SEO & Advanced' },
] as const;

// ── Smart Planning default sections ──────────────────────────────────
interface ItinerarySection {
  id: string;
  visible: boolean;
  label: string;
}

const DEFAULT_ITINERARY_SECTIONS: ItinerarySection[] = [
  { id: 'overview',       visible: true, label: '' },
  { id: 'requirements',   visible: true, label: '' },
  { id: 'itinerary',      visible: true, label: '' },
  { id: 'culture',        visible: true, label: '' },
  { id: 'food',           visible: true, label: '' },
  { id: 'tips',           visible: true, label: '' },
  { id: 'neighborhoods',  visible: true, label: '' },
  { id: 'seasonal',       visible: true, label: '' },
  { id: 'share',          visible: true, label: '' },
];

const SECTION_DEFAULTS: Record<string, { label: string; icon: string; locked?: boolean }> = {
  overview:       { label: 'Overview',            icon: '📋', locked: true },
  requirements:   { label: 'Travel Requirements', icon: '📄' },
  itinerary:      { label: 'Day-by-Day Itinerary', icon: '🗓️', locked: true },
  culture:        { label: 'Cultural Etiquette',  icon: '🎭' },
  food:           { label: 'Food Recommendations', icon: '🍴' },
  tips:           { label: 'Local Tips',          icon: '💡' },
  neighborhoods:  { label: 'Neighborhood Guide',  icon: '🏘️' },
  seasonal:       { label: 'Seasonal Notes',      icon: '🌤️' },
  share:          { label: 'Share Itinerary',     icon: '🔗' },
  // 'download' was previously a togglable section but the Results page
  // has no corresponding section-download block — the download buttons
  // live in the overview action bar and are always available. Keeping
  // the entry here so existing tenants who saved this id in their
  // config don't crash on render; the runtime filter below hides it.
  download:       { label: 'Download',            icon: '⬇️' },
};

/** Legacy section IDs that should be hidden from the editor. They might
 *  still appear in saved tenant configs but the App-side Results page
 *  does nothing with them, so they're inert. Filter at render so existing
 *  configs don't trigger DB writes — the value persists harmlessly. */
const LEGACY_SECTION_IDS = new Set(['download']);

type BrandingTab = typeof BRANDING_TABS[number]['id'];

const FEATURE_CARD_LAYOUTS = [
  { value: 'grid', label: 'Grid', emoji: '⊞' },
  { value: 'horizontal', label: 'Horizontal', emoji: '☰' },
  { value: 'carousel', label: 'Carousel', emoji: '⟷' },
  { value: 'badges', label: 'Badges', emoji: '◉' },
] as const;

const INFO_CARD_LAYOUTS = [
  { value: 'grid', label: 'Grid', emoji: '⊞' },
  { value: 'horizontal', label: 'Horizontal', emoji: '☰' },
  { value: 'carousel', label: 'Carousel', emoji: '⟷' },
];

const DESTINATION_LAYOUTS = [
  { value: 'grid', label: 'Grid', emoji: '⊞' },
  { value: 'carousel', label: 'Carousel', emoji: '⟷' },
  { value: 'list', label: 'List', emoji: '☰' },
] as const;

// Sample destinations used in splash preview for custom layouts
const PREVIEW_SAMPLE_DESTS = [
  { name: 'Bali', sub: 'Indonesia', price: '$500+', season: 'Apr-Oct' },
  { name: 'Tokyo', sub: 'Japan', price: '$800+', season: 'Mar-May' },
  { name: 'Paris', sub: 'France', price: '$600+', season: 'Jun-Sep' },
  { name: 'Dubai', sub: 'UAE', price: '$700+', season: 'Nov-Mar' },
];

const YOUTUBE_FEED_LAYOUTS = [
  { value: 'grid', label: 'Grid', emoji: '⊞' },
  { value: 'carousel', label: 'Carousel', emoji: '⟷' },
  { value: 'list', label: 'List', emoji: '☰' },
];
const BLOG_FEED_LAYOUTS = [
  { value: 'grid', label: 'Grid', emoji: '⊞' },
  { value: 'carousel', label: 'Carousel', emoji: '⟷' },
  { value: 'list', label: 'List', emoji: '☰' },
];
const INSTAGRAM_FEED_LAYOUTS = [
  { value: 'grid', label: 'Grid', emoji: '⊞' },
  { value: 'carousel', label: 'Carousel', emoji: '⟷' },
];
const TIKTOK_FEED_LAYOUTS = [
  { value: 'grid', label: 'Grid', emoji: '⊞' },
  { value: 'carousel', label: 'Carousel', emoji: '⟷' },
];
const CURATED_TRIPS_LAYOUTS = [
  { value: 'grid', label: 'Grid', emoji: '⊞' },
  { value: 'carousel', label: 'Carousel', emoji: '⟷' },
  { value: 'list', label: 'List', emoji: '☰' },
];

// Icons available for custom info cards
const INFO_CARD_ICONS: { name: string; label: string; component: LucideIcon }[] = [
  { name: 'heart', label: 'Heart', component: Heart },
  { name: 'star', label: 'Star', component: Star },
  { name: 'globe', label: 'Globe', component: GlobeIcon },
  { name: 'compass', label: 'Compass', component: Compass },
  { name: 'flag', label: 'Flag', component: Flag },
  { name: 'shield', label: 'Shield', component: Shield },
  { name: 'lightbulb', label: 'Lightbulb', component: Lightbulb },
  { name: 'book', label: 'Book', component: BookOpen },
  { name: 'coffee', label: 'Coffee', component: Coffee },
  { name: 'utensils', label: 'Utensils', component: UtensilsCrossed },
  { name: 'music', label: 'Music', component: Music },
  { name: 'sun', label: 'Sun', component: Sun },
  { name: 'moon', label: 'Moon', component: Moon },
  { name: 'camera', label: 'Camera', component: Camera },
  { name: 'phone', label: 'Phone', component: Phone },
  { name: 'mail', label: 'Mail', component: Mail },
  { name: 'clock', label: 'Clock', component: Clock },
  { name: 'calendar', label: 'Calendar', component: Calendar },
  { name: 'award', label: 'Award', component: Award },
  { name: 'thumbsup', label: 'Thumbs Up', component: ThumbsUp },
  { name: 'message', label: 'Message', component: MessageCircle },
  { name: 'plane', label: 'Plane', component: Plane },
  { name: 'mountain', label: 'Mountain', component: Mountain },
  { name: 'tree', label: 'Tree', component: TreePine },
  { name: 'landmark', label: 'Landmark', component: Landmark },
  { name: 'handshake', label: 'Handshake', component: Handshake },
  { name: 'key', label: 'Key', component: Key },
  { name: 'home', label: 'Home', component: Home },
  { name: 'brain', label: 'Brain', component: Brain },
  { name: 'eye', label: 'Eye', component: Eye },
];

// Map field names to their branding tab for auto-navigation
const FIELD_TAB_MAP: Record<string, 'design' | 'splash' | 'destinations' | 'cards' | 'emails' | 'seo'> = {
  app_name: 'design', primary_color: 'design', secondary_color: 'design',
  font_family: 'design', logo_url: 'design', logo_dark_url: 'design', favicon_url: 'design',
  welcome_message: 'design',
  hero_headline: 'splash', hero_subtitle: 'splash', cta_label: 'splash', company_name: 'splash',
  bio_heading: 'splash', bio_text: 'splash', bio_image_url: 'splash',
  show_destinations: 'destinations', destinations_layout: 'destinations',
  use_custom_destinations: 'destinations', custom_destinations_layout: 'destinations',
  feature_cards: 'cards', feature_cards_heading: 'cards', feature_cards_layout: 'cards',
  show_curated_trips: 'splash', curated_trips_heading: 'splash', curated_trips_subtitle: 'splash', curated_trips_layout: 'splash',
  info_cards_mode: 'cards', info_cards_layout: 'cards', info_cards_heading: 'cards', info_cards_subtitle: 'cards',
  support_email: 'emails', support_url: 'emails',
  booking_inquiry_email: 'emails', information_email: 'emails',
  facebook_url: 'emails', instagram_url: 'emails', x_url: 'emails',
  youtube_url: 'emails', linkedin_url: 'emails',
  meta_title: 'seo', meta_description: 'seo', custom_css: 'seo',
  blog_source_url: 'splash', blog_source_type: 'splash', show_blog_feed: 'splash',
  youtube_channel_id: 'splash', youtube_handle: 'splash', show_youtube_feed: 'splash',
  instagram_handle: 'splash', show_instagram_feed: 'splash',
  youtube_feed_layout: 'splash', blog_feed_layout: 'splash', instagram_feed_layout: 'splash',
  youtube_feed_count: 'splash', blog_feed_count: 'splash', instagram_feed_count: 'splash', latest_feed_count: 'splash',
  trustpilot_business_unit_id: 'splash', trustpilot_template_id: 'splash', trustpilot_review_url: 'splash',
};

const GOOGLE_FONTS_PROFESSIONAL = [
  'DM Sans', 'Inter', 'Outfit', 'Plus Jakarta Sans', 'Poppins',
  'Nunito', 'Lato', 'Open Sans', 'Montserrat', 'Raleway',
  'Source Sans 3', 'Work Sans', 'Rubik', 'Manrope', 'Sora',
];

const GOOGLE_FONTS_ARTISTIC = [
  'Playfair Display', 'Cormorant Garamond', 'Libre Baskerville', 'Crimson Text',
  'Abril Fatface', 'Cinzel', 'Josefin Sans', 'Quicksand', 'Comfortaa',
  'Righteous', 'Pacifico', 'Dancing Script', 'Caveat', 'Lobster',
  'Satisfy', 'Great Vibes', 'Sacramento', 'Amatic SC', 'Permanent Marker',
  'Bodoni Moda', 'Cormorant', 'EB Garamond', 'Spectral', 'Bitter',
];

const GOOGLE_FONTS = [...GOOGLE_FONTS_PROFESSIONAL, ...GOOGLE_FONTS_ARTISTIC];

const COLOR_GROUPS = [
  { label: 'Vibrant', colors: [
    '#ef4444', '#f97316', '#f59e0b', '#84cc16', '#10b981',
    '#0d9488', '#06b6d4', '#3b82f6', '#6366f1', '#8b5cf6',
    '#a855f7', '#ec4899', '#f43f5e',
  ]},
  { label: 'Corporate', colors: [
    '#1e3a5f', '#2c5282', '#1a365d', '#234e52', '#22543d',
    '#742a2a', '#7b341e', '#44337a', '#64748b', '#2d3748',
  ]},
  { label: 'Luxury', colors: [
    '#b8860b', '#c9a84c', '#5b2c6f', '#1b4332', '#4a1942',
    '#8b6914', '#2c3e50', '#6b4226', '#3d0c11', '#1c1c1c',
  ]},
];
const PRESET_COLORS = COLOR_GROUPS.flatMap(g => g.colors);

// Feature card options for the splash page customizer
const FEATURE_CARD_OPTIONS: { slug: string; title: string; icon: LucideIcon; description: string }[] = [
  { slug: 'ai_trip_builder',   title: 'Smart Planning',        icon: Brain,          description: 'Build culture-aware itineraries effortlessly' },
  { slug: 'budget_manager',    title: 'Budget Tracking',        icon: Wallet,         description: 'Track expenses and stay on budget' },
  { slug: 'collaboration',     title: 'Live Collaboration',     icon: GlobeIcon,      description: 'Plan together in real-time' },
  { slug: 'expense_splitting', title: 'Expense Splitting',      icon: Users,          description: 'Split costs with companions' },
  { slug: 'cultural_hub',      title: 'Cultural Insights',      icon: Heart,          description: 'Travel respectfully with local tips' },
  { slug: 'voice_interpreter', title: 'Voice Interpreter',      icon: Mic,            description: 'Real-time translation on the go' },
  { slug: 'packing_list',      title: 'Smart Packing',          icon: Package,        description: 'Never forget travel essentials' },
  { slug: 'receipt_ocr',       title: 'Receipt Scanner',        icon: Camera,         description: 'Scan and track receipts' },
  { slug: 'doc_scanner',       title: 'Document Scanner',       icon: FileText,       description: 'Scan travel documents' },
  { slug: 'live_dashboard',    title: 'Live Dashboard',         icon: BarChart3,      description: 'Real-time trip overview' },
  { slug: 'offline_mode',      title: 'Offline Mode',           icon: WifiOff,        description: 'Access trips without internet' },
  { slug: 'smart_companion',   title: 'Smart Companion',        icon: SparklesLucide, description: 'AI-powered travel assistant' },
  { slug: 'local_agent',       title: 'Local Agent',            icon: MapPinLucide,   description: 'Connect with local experts' },
  { slug: 'zoom_meetings',     title: 'Schedule Meetings',      icon: VideoLucide,    description: 'Book video calls with agents' },
  { slug: 'visual_context',    title: 'Visual Context',         icon: Eye,            description: 'Image recognition for places' },
  // ── New: remaining plan features ──
  { slug: 'max_destinations',    title: 'Destination Explorer',   icon: Compass,        description: 'Plan multi-city adventures with ease' },
  { slug: 'geofencing',          title: 'Smart Alerts',           icon: Navigation,     description: 'Get location-based tips as you explore' },
  // presence_indicators card retired alongside the feature.
  // activity_comments + activity_voting cards retired; see FEATURE_META.
  { slug: 'advanced_roles',      title: 'Team Roles',             icon: Shield,         description: 'Assign organizer, editor and viewer roles' },
  { slug: 'discovery_gem',       title: 'Discovery Gem',          icon: Gem,            description: 'Discover local secrets and off-the-beaten-path spots' },
  { slug: 'max_expenses',        title: 'Expense Tracker',        icon: CreditCard,     description: 'Log every expense to stay on top of your budget' },
  { slug: 'max_shared_budgets',  title: 'Shared Budgets',         icon: Handshake,      description: 'Pool funds and track shared spending with companions' },
  { slug: 'category_budgeting',  title: 'Budget Categories',      icon: PieChart,       description: 'Organize spending by food, transport, activities and more' },
  { slug: 'whatsapp_planning',   title: 'Plan on WhatsApp',       icon: MessageCircle,  description: 'Build a whole trip in a WhatsApp chat — and ask about your trips anytime' },
];

/**
 * Maps Branding card slugs → plan_feature_defaults keys (FEATURE_META).
 * Slugs that already match (expense_splitting, voice_interpreter, offline_mode,
 * zoom_meetings) are omitted — the lookup falls through to a direct match.
 * Slugs with no plan key (packing_list) are treated as always-available.
 */
const BRANDING_TO_PLAN_KEY: Record<string, string> = {
  ai_trip_builder:  'max_active_trips',
  budget_manager:   'max_budget_planners',
  collaboration:    'max_collaborators',
  cultural_hub:     'max_cultural_queries',
  receipt_ocr:      'max_receipt_ocr',
  doc_scanner:      'max_doc_scans',
  smart_companion:  'max_companion_queries',
  local_agent:      'ai_chatbots',
  visual_context:   'max_visual_lookups',
  live_dashboard:   'max_session_sharing',
};

// Preset info card categories (maps to culturalData.ts slugs)
const INFO_CARD_PRESETS: { slug: string; title: string; icon: LucideIcon; description: string }[] = [
  { slug: 'greetings',    title: 'Greetings & Communication', icon: Handshake,         description: 'Local greetings, gestures & etiquette' },
  { slug: 'dining',       title: 'Dining Etiquette',          icon: UtensilsCrossed,   description: 'Table manners & food customs' },
  { slug: 'social_norms', title: 'Social Norms',              icon: Users,             description: 'Behavioural expectations & social cues' },
  { slug: 'taboos',       title: 'Cultural Taboos',           icon: Shield,            description: 'Things to avoid in local culture' },
  { slug: 'festivals',    title: 'Festivals & Celebrations',  icon: Calendar,          description: 'Major holidays & local events' },
  { slug: 'safety',       title: 'Safety & Awareness',        icon: Eye,               description: 'Safety tips & local awareness' },
  { slug: 'dress',        title: 'Dress Expectations',        icon: Award,             description: 'Dress code & appearance norms' },
  { slug: 'food',         title: 'Food Culture',              icon: Coffee,            description: 'Local cuisine & food traditions' },
];

export function BrandingPage({ tenantId }: { tenantId: string }) {
  const { selectedTenant: tenant, updateBranding, loading } = usePlatformStore();
  const branding = tenant?.branding as BrandingConfig | undefined;

  const [form, setForm] = useState({
    app_name: '', primary_color: '#0d9488', secondary_color: '',
    font_family: '', logo_url: '', logo_dark_url: '', favicon_url: '',
    welcome_message: '', support_email: '', support_url: '',
    booking_inquiry_email: '', information_email: '',
    registration_email: '', contact_email: '', info_email: '', billing_email: '', partner_email: '',
    meta_title: '', meta_description: '', custom_css: '',
    // Splash page branding
    hero_headline: '', hero_subtitle: '', cta_label: '', company_name: '',
    // Influencer / About section
    bio_heading: '', bio_text: '', bio_image_url: '',
    facebook_url: '', instagram_url: '', x_url: '', youtube_url: '', linkedin_url: '',
    show_destinations: true,
    destinations_layout: 'grid',
    show_curated_trips: true,
    curated_trips_heading: '',
    curated_trips_subtitle: '',
    curated_trips_layout: 'grid',
    use_custom_destinations: false,
    custom_destinations_layout: 'cards',
    feature_cards: [] as string[],
    feature_cards_heading: '',
    feature_cards_layout: 'grid',
    info_cards_mode: 'preset',
    info_cards_layout: 'grid',
    info_cards_presets: [] as string[],
    info_cards_custom: [] as { title: string; description: string; icon?: string }[],
    info_cards_heading: '', info_cards_subtitle: '',
    // Content feeds
    blog_source_url: '', blog_source_type: 'rss' as string,
    show_blog_feed: false, youtube_channel_id: '', youtube_handle: '',
    show_youtube_feed: false, instagram_handle: '', show_instagram_feed: false,
    tiktok_handle: '', show_tiktok_feed: false, tiktok_video_urls: [] as string[],
    youtube_feed_layout: 'grid', blog_feed_layout: 'grid', instagram_feed_layout: 'grid', tiktok_feed_layout: 'grid',
    youtube_feed_count: 6, blog_feed_count: 6, instagram_feed_count: 9, tiktok_feed_count: 9, latest_feed_count: 3,
    // Smart Planning (itinerary page) branding
    itinerary_page_heading: 'Your Culturally Affluent Plan',
    itinerary_show_pills: true,
    itinerary_show_city_images: true,
    itinerary_sections: [...DEFAULT_ITINERARY_SECTIONS] as ItinerarySection[],
    // Trustpilot — per-tenant review widget. Blank = opt-out, every
    // Trustpilot CTA across the App hides on this tenant's domain.
    trustpilot_business_unit_id: '',
    trustpilot_template_id: '',
    trustpilot_review_url: '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<BrandingTab>('design');
  const [highlightedField, setHighlightedField] = useState<string | null>(null);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── DnD sensors for Smart Planning section reordering ──────────────
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // ── Plan-derived feature flags ────────────────────────────────────
  // Read features from the tenant's subscribed plan (plan_feature_defaults)
  // instead of the per-tenant feature_configs copy, so changes to the
  // plan are always reflected without needing a sync/propagation step.
  const [planFeatures, setPlanFeatures] = useState<Record<string, boolean> | null>(null);
  useEffect(() => {
    if (!tenant?.plan_id) { setPlanFeatures(null); return; }
    fetchPlanFeatureDefaults(tenant.plan_id).then(rows => {
      // Build a raw map keyed by FEATURE_META keys
      const raw: Record<string, boolean> = {};
      for (const r of rows) raw[r.feature_key] = r.is_enabled;

      // Re-key by branding slug so planFeatures[card.slug] lookups work
      const map: Record<string, boolean> = {};
      for (const card of FEATURE_CARD_OPTIONS) {
        const planKey = BRANDING_TO_PLAN_KEY[card.slug];
        if (planKey && planKey in raw) {
          map[card.slug] = raw[planKey];       // mapped key found in plan
        } else if (card.slug in raw) {
          map[card.slug] = raw[card.slug];     // direct match (e.g. offline_mode)
        } else {
          map[card.slug] = true;               // not plan-controlled → always available
        }
      }
      setPlanFeatures(map);
    }).catch(console.error);
  }, [tenant?.plan_id]);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    };
  }, []);

  // Populate form from DB
  useEffect(() => {
    if (branding) {
      setForm({
        app_name: branding.app_name || '',
        primary_color: branding.primary_color || '#0d9488',
        secondary_color: branding.secondary_color || '',
        font_family: branding.font_family || '',
        logo_url: branding.logo_url || '',
        logo_dark_url: branding.logo_dark_url || '',
        favicon_url: branding.favicon_url || '',
        welcome_message: branding.welcome_message || '',
        support_email: branding.support_email || '',
        support_url: branding.support_url || '',
        booking_inquiry_email: branding.booking_inquiry_email || '',
        information_email: branding.information_email || '',
        registration_email: branding.registration_email || '',
        contact_email: branding.contact_email || '',
        info_email: branding.info_email || '',
        billing_email: branding.billing_email || '',
        partner_email: branding.partner_email || '',
        meta_title: branding.meta_title || '',
        meta_description: branding.meta_description || '',
        custom_css: branding.custom_css || '',
        // Splash page branding
        hero_headline: branding.hero_headline || '',
        hero_subtitle: branding.hero_subtitle || '',
        cta_label: branding.cta_label || '',
        company_name: branding.company_name || '',
        // About / influencer bio
        bio_heading: (branding as any).bio_heading || '',
        bio_text: (branding as any).bio_text || '',
        bio_image_url: (branding as any).bio_image_url || '',
        facebook_url: branding.facebook_url || '',
        instagram_url: branding.instagram_url || '',
        x_url: branding.x_url || '',
        youtube_url: branding.youtube_url || '',
        linkedin_url: branding.linkedin_url || '',
        show_destinations: branding.show_destinations ?? true,
        destinations_layout: branding.destinations_layout || 'grid',
        show_curated_trips: (branding as any).show_curated_trips ?? true,
        curated_trips_heading: (branding as any).curated_trips_heading || '',
        curated_trips_subtitle: (branding as any).curated_trips_subtitle || '',
        curated_trips_layout: (branding as any).curated_trips_layout || 'grid',
        use_custom_destinations: (branding as any).use_custom_destinations ?? false,
        custom_destinations_layout: (branding as any).custom_destinations_layout || 'cards',
        feature_cards: (branding.feature_cards as string[]) || [],
        feature_cards_heading: branding.feature_cards_heading || '',
        feature_cards_layout: branding.feature_cards_layout || 'grid',
        info_cards_mode: branding.info_cards_mode || 'preset',
        info_cards_layout: branding.info_cards_layout || 'grid',
        info_cards_presets: (branding.info_cards_presets as string[]) || [],
        info_cards_custom: (branding.info_cards_custom as { title: string; description: string; icon?: string }[]) || [],
        info_cards_heading: branding.info_cards_heading || '',
        info_cards_subtitle: branding.info_cards_subtitle || '',
        // Content feeds
        blog_source_url: (branding as any).blog_source_url || '',
        blog_source_type: (branding as any).blog_source_type || 'rss',
        show_blog_feed: (branding as any).show_blog_feed ?? false,
        youtube_channel_id: (branding as any).youtube_channel_id || '',
        youtube_handle: (branding as any).youtube_handle || '',
        show_youtube_feed: (branding as any).show_youtube_feed ?? false,
        instagram_handle: (branding as any).instagram_handle || '',
        show_instagram_feed: (branding as any).show_instagram_feed ?? false,
        tiktok_handle: (branding as any).tiktok_handle || '',
        show_tiktok_feed: (branding as any).show_tiktok_feed ?? false,
        tiktok_video_urls: ((branding as any).tiktok_video_urls as string[]) || [],
        youtube_feed_layout: (branding as any).youtube_feed_layout || 'grid',
        blog_feed_layout: (branding as any).blog_feed_layout || 'grid',
        instagram_feed_layout: (branding as any).instagram_feed_layout || 'grid',
        tiktok_feed_layout: (branding as any).tiktok_feed_layout || 'grid',
        youtube_feed_count: (branding as any).youtube_feed_count ?? 6,
        blog_feed_count: (branding as any).blog_feed_count ?? 6,
        instagram_feed_count: (branding as any).instagram_feed_count ?? 9,
        tiktok_feed_count: (branding as any).tiktok_feed_count ?? 9,
        latest_feed_count: (branding as any).latest_feed_count ?? 3,
        // Smart Planning
        itinerary_page_heading: (branding as any).itinerary_page_heading || 'Your Culturally Affluent Plan',
        itinerary_show_pills: (branding as any).itinerary_show_pills ?? true,
        itinerary_show_city_images: (branding as any).itinerary_show_city_images ?? true,
        itinerary_sections: ((branding as any).itinerary_sections as ItinerarySection[]) || [...DEFAULT_ITINERARY_SECTIONS],
        // Trustpilot
        trustpilot_business_unit_id: (branding as any).trustpilot_business_unit_id || '',
        trustpilot_template_id: (branding as any).trustpilot_template_id || '',
        trustpilot_review_url: (branding as any).trustpilot_review_url || '',
      });
    }
  }, [branding]);

  const set = (field: string, value: any) => setForm(prev => ({ ...prev, [field]: value }));

  // Called by BrandingAssistant after applying a suggestion — switches tab, scrolls, highlights
  const onFieldApplied = useCallback((field: string) => {
    const tab = FIELD_TAB_MAP[field];
    if (tab) setActiveTab(tab);
    // Clear previous highlight timer
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    // Wait for tab to render, then scroll + highlight
    setTimeout(() => {
      setHighlightedField(field);
      const el = document.querySelector(`[data-field="${field}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Remove highlight after 30 seconds
      highlightTimerRef.current = setTimeout(() => setHighlightedField(null), 30000);
    }, 150);
  }, []);

  // Load Google Font when font_family changes (for live preview)
  useEffect(() => {
    if (!form.font_family) return;
    const id = `gfont-${form.font_family.replace(/\s+/g, '-').toLowerCase()}`;
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(form.font_family)}:wght@400;500;600;700&display=swap`;
    document.head.appendChild(link);
  }, [form.font_family]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setSaveError(null);
    try {
      await updateBranding(tenantId, {
        app_name: form.app_name || undefined,
        primary_color: form.primary_color || undefined,
        secondary_color: form.secondary_color || null,
        font_family: form.font_family || null,
        logo_url: form.logo_url || null,
        logo_dark_url: form.logo_dark_url || null,
        favicon_url: form.favicon_url || null,
        welcome_message: form.welcome_message || null,
        support_email: form.support_email || null,
        support_url: form.support_url || null,
        booking_inquiry_email: form.booking_inquiry_email || null,
        information_email: form.information_email || null,
        registration_email: form.registration_email || null,
        contact_email: form.contact_email || null,
        info_email: form.info_email || null,
        billing_email: form.billing_email || null,
        partner_email: form.partner_email || null,
        meta_title: form.meta_title || null,
        meta_description: form.meta_description || null,
        custom_css: form.custom_css || null,
        // Splash page branding
        hero_headline: form.hero_headline || null,
        hero_subtitle: form.hero_subtitle || null,
        cta_label: form.cta_label || null,
        company_name: form.company_name || null,
        // About / influencer bio
        bio_heading: form.bio_heading || null,
        bio_text: form.bio_text || null,
        bio_image_url: form.bio_image_url || null,
        facebook_url: form.facebook_url || null,
        instagram_url: form.instagram_url || null,
        x_url: form.x_url || null,
        youtube_url: form.youtube_url || null,
        linkedin_url: form.linkedin_url || null,
        show_destinations: form.show_destinations,
        destinations_layout: form.destinations_layout || 'grid',
        show_curated_trips: form.show_curated_trips,
        curated_trips_heading: form.curated_trips_heading || null,
        curated_trips_subtitle: form.curated_trips_subtitle || null,
        curated_trips_layout: form.curated_trips_layout || 'grid',
        use_custom_destinations: form.use_custom_destinations,
        custom_destinations_layout: form.custom_destinations_layout || 'cards',
        feature_cards: form.feature_cards.length > 0 ? form.feature_cards : null,
        feature_cards_heading: form.feature_cards_heading || null,
        feature_cards_layout: form.feature_cards_layout || 'grid',
        info_cards_mode: form.info_cards_mode || 'preset',
        info_cards_layout: form.info_cards_layout || 'grid',
        info_cards_presets: form.info_cards_presets.length > 0 ? form.info_cards_presets : null,
        info_cards_custom: form.info_cards_custom.length > 0 ? form.info_cards_custom : null,
        info_cards_heading: form.info_cards_heading || null,
        info_cards_subtitle: form.info_cards_subtitle || null,
        // Content feeds
        blog_source_url: form.blog_source_url || null,
        blog_source_type: form.blog_source_type || 'rss',
        show_blog_feed: form.show_blog_feed,
        youtube_channel_id: form.youtube_channel_id || null,
        youtube_handle: form.youtube_handle || null,
        show_youtube_feed: form.show_youtube_feed,
        instagram_handle: form.instagram_handle || null,
        show_instagram_feed: form.show_instagram_feed,
        tiktok_handle: form.tiktok_handle || null,
        show_tiktok_feed: form.show_tiktok_feed,
        tiktok_video_urls: form.tiktok_video_urls,
        youtube_feed_layout: form.youtube_feed_layout || 'grid',
        blog_feed_layout: form.blog_feed_layout || 'grid',
        instagram_feed_layout: form.instagram_feed_layout || 'grid',
        tiktok_feed_layout: form.tiktok_feed_layout || 'grid',
        youtube_feed_count: form.youtube_feed_count,
        blog_feed_count: form.blog_feed_count,
        instagram_feed_count: form.instagram_feed_count,
        tiktok_feed_count: form.tiktok_feed_count,
        latest_feed_count: form.latest_feed_count,
        // Smart Planning
        itinerary_page_heading: form.itinerary_page_heading || null,
        itinerary_show_pills: form.itinerary_show_pills,
        itinerary_show_city_images: form.itinerary_show_city_images,
        itinerary_sections: form.itinerary_sections.length > 0 ? form.itinerary_sections : null,
        // Trustpilot — blank inputs persist as NULL so the App hides
        // every CTA when the tenant clears their config.
        trustpilot_business_unit_id: form.trustpilot_business_unit_id || null,
        trustpilot_template_id: form.trustpilot_template_id || null,
        trustpilot_review_url: form.trustpilot_review_url || null,
      } as any);
      setSaved(true);
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setSaved(false), 3000);
    } catch (err: any) {
      console.error('[Branding] Save failed:', err);
      setSaveError(err.message || 'Failed to save branding. Check that all DB migrations have been run.');
    } finally { setSaving(false); }
  };

  if (!branding) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;

  // Highlight style for fields when BrandingAssistant applies a suggestion
  const highlightStyle: React.CSSProperties = {
    boxShadow: '0 0 0 3px rgba(16, 185, 129, 0.5), 0 0 12px rgba(16, 185, 129, 0.25)',
    borderRadius: 8,
    transition: 'box-shadow 0.3s ease',
  };

  // Helper to wrap form fields with data-field attribute and highlight
  const fieldProps = (field: string) => ({
    'data-field': field,
    style: highlightedField === field ? highlightStyle : undefined,
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-surface-900">Branding</h1>
          <p className="text-sm text-surface-500 mt-1">Customise how your app looks to your users</p>
        </div>
        <div className="flex items-center gap-3">
          {saved && <span className="text-sm text-emerald-400">Saved!</span>}
          {saveError && <span className="text-sm text-red-600 dark:text-red-400 max-w-md truncate" title={saveError}>⚠ {saveError}</span>}
          <button onClick={handleSave} disabled={saving} className="btn-primary text-sm">
            {saving ? <Spinner size="sm" /> : null}
            Save Changes
          </button>
        </div>
      </div>

      {/* ── Tab Navigation (Folder Tabs) ─────────────────────────────── */}
      <div className="border-b border-surface-700">
        <div className="flex gap-1">
          {BRANDING_TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={cn(
                'py-2.5 px-4 text-sm font-semibold text-center transition-all border border-b-0 rounded-t-lg -mb-px',
                activeTab === t.id
                  ? 'border-emerald-500/50 badge-emerald'
                  : 'border-transparent text-surface-500 hover:text-surface-300 hover:bg-surface-800/50'
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        {/* ── Editor (left) ──────────────────────────────────────────────── */}
        <div className="xl:col-span-3 space-y-5">

          {/* ═══════════════ TAB 1: Brand & Design ═══════════════ */}
          {activeTab === 'design' && <>
          {/* Identity */}
          <Section title="Identity">
            <div {...fieldProps('app_name')}>
              <Field label="App Name" sub="Replaces 'DiscoveryGem' everywhere in the app">
                <input className="input-field" value={form.app_name} onChange={e => set('app_name', e.target.value)} placeholder="My Travel App" />
              </Field>
            </div>
            <div {...fieldProps('welcome_message')}>
              <Field label="Welcome Message" sub="Shown on the home screen">
                <input className="input-field" value={form.welcome_message} onChange={e => set('welcome_message', e.target.value)} placeholder="Plan your next adventure" />
              </Field>
            </div>
          </Section>

          {/* Colours */}
          <Section title="Colours">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div {...fieldProps('primary_color')}>
              <Field label="Primary Colour" sub="Buttons, links, accents">
                <div className="flex items-center gap-3">
                  <input type="color" value={form.primary_color} onChange={e => set('primary_color', e.target.value)}
                    className="w-10 h-10 rounded-lg border-2 border-white/10 cursor-pointer bg-transparent shrink-0" />
                  <input className="input-field w-28 font-mono text-sm" value={form.primary_color}
                    onChange={e => set('primary_color', e.target.value)} />
                  <div className="w-8 h-8 rounded-lg shrink-0" style={{ backgroundColor: form.primary_color }} />
                </div>
                <div className="mt-3 space-y-2.5">
                  {COLOR_GROUPS.map(group => (
                    <div key={group.label}>
                      <p className="text-[10px] uppercase tracking-wider text-surface-500 mb-1.5">{group.label}</p>
                      <div className="flex gap-1.5 flex-wrap">
                        {group.colors.map(c => (
                          <button key={c} onClick={() => set('primary_color', c)}
                            className={cn('w-7 h-7 rounded-lg border-2 transition-all hover:scale-110 flex items-center justify-center',
                              form.primary_color === c ? 'border-white scale-110 ring-2 ring-white/30' : 'border-transparent hover:border-white/30')}
                            style={{ backgroundColor: c }}>
                            {form.primary_color === c && <Check size={12} className="text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]" />}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </Field>
              </div>
              <div {...fieldProps('secondary_color')}>
              <Field label="Secondary Colour" sub="Hover states, badges">
                <div className="flex items-center gap-3">
                  <input type="color" value={form.secondary_color || form.primary_color}
                    onChange={e => set('secondary_color', e.target.value)}
                    className="w-10 h-10 rounded-lg border-2 border-white/10 cursor-pointer bg-transparent shrink-0" />
                  <input className="input-field w-28 font-mono text-sm" value={form.secondary_color}
                    onChange={e => set('secondary_color', e.target.value)} placeholder="Auto from primary" />
                  {form.secondary_color && (
                    <button onClick={() => set('secondary_color', '')}
                      className="text-xs text-surface-500 hover:text-surface-700 transition-colors">Clear</button>
                  )}
                </div>
                <div className="mt-3 space-y-2.5">
                  {COLOR_GROUPS.map(group => (
                    <div key={group.label}>
                      <p className="text-[10px] uppercase tracking-wider text-surface-500 mb-1.5">{group.label}</p>
                      <div className="flex gap-1.5 flex-wrap">
                        {group.colors.map(c => (
                          <button key={c} onClick={() => set('secondary_color', c)}
                            className={cn('w-7 h-7 rounded-lg border-2 transition-all hover:scale-110 flex items-center justify-center',
                              form.secondary_color === c ? 'border-white scale-110 ring-2 ring-white/30' : 'border-transparent hover:border-white/30')}
                            style={{ backgroundColor: c }}>
                            {form.secondary_color === c && <Check size={12} className="text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]" />}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </Field>
              </div>
            </div>
          </Section>

          {/* Typography */}
          <Section title="Typography">
            <div {...fieldProps('font_family')}>
            <Field label="Font Family" sub="Loaded from Google Fonts — choose Professional or Artistic styles">
              <FontFamilyDropdown value={form.font_family} onChange={(v) => set('font_family', v)} />
              {form.font_family && (
                <div className="mt-3 p-3 rounded-lg border border-white/10 bg-white/5">
                  <p className="text-xs text-white/50 mb-1">Preview:</p>
                  <p style={{ fontFamily: `'${form.font_family}', sans-serif`, color: '#e2e8f0' }} className="text-lg">
                    The quick brown fox jumps over the lazy dog
                  </p>
                  <p style={{ fontFamily: `'${form.font_family}', sans-serif`, color: '#94a3b8' }} className="text-sm mt-1">
                    Plan your next adventure — 0123456789
                  </p>
                </div>
              )}
            </Field>
            </div>
          </Section>

          {/* Logos */}
          <Section title="Logos & Icons">
            {/* Logo library — pick a platform-curated SVG and have light,
                dark, and favicon set in one click. Tenant can still upload
                custom files below to override. */}
            <LogoLibraryPicker
              logoUrl={form.logo_url}
              onPick={(patch) => {
                set('logo_url',      patch.logo_url);
                set('logo_dark_url', patch.logo_dark_url);
                set('favicon_url',   patch.favicon_url);
              }}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <LogoUploader
                value={form.logo_url}
                onChange={(v) => set('logo_url', v)}
                tenantId={tenantId}
                purpose="logo"
                label="Logo"
                sub="Primary logo — shown on light backgrounds & PWA install"
                recommendedSize="512×512 PNG · transparent background"
                previewBg="light"
              />
              <LogoUploader
                value={form.logo_dark_url}
                onChange={(v) => set('logo_dark_url', v)}
                tenantId={tenantId}
                purpose="logo-dark"
                label="Dark Mode Logo"
                sub="Light/white version for dark backgrounds"
                recommendedSize="512×512 PNG · transparent background"
                previewBg="dark"
              />
              <LogoUploader
                value={form.favicon_url}
                onChange={(v) => set('favicon_url', v)}
                tenantId={tenantId}
                purpose="favicon"
                label="Favicon"
                sub="Browser tab icon — square, simple, readable at 16px"
                recommendedSize="256×256 PNG or SVG · 32×32 minimum"
              />
            </div>
          </Section>

          </>}

          {/* ═══════════════ TAB 4: Emails & Social ═══════════════ */}
          {activeTab === 'emails' && <>
          {/* Support & Contact */}
          <Section title="Support & Contact">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Support Email"><input className="input-field text-sm" value={form.support_email} onChange={e => set('support_email', e.target.value)} placeholder="support@example.com" /></Field>
              <Field label="Support URL"><input className="input-field text-sm" value={form.support_url} onChange={e => set('support_url', e.target.value)} placeholder="https://help.example.com" /></Field>
              <Field label="Booking Inquiry Email" sub="Where booking inquiries are sent (falls back to admin emails)">
                <input className="input-field text-sm" value={form.booking_inquiry_email} onChange={e => set('booking_inquiry_email', e.target.value)} placeholder="bookings@example.com" />
              </Field>
              <Field label="Information Email" sub="General info/contact email shown to users">
                <input className="input-field text-sm" value={form.information_email} onChange={e => set('information_email', e.target.value)} placeholder="info@example.com" />
              </Field>
            </div>
          </Section>

          {/* Email Configuration */}
          <Section title="Email Configuration">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Registration Email" sub="Business lead / registration notifications">
                <input className="input-field text-sm" value={form.registration_email} onChange={e => set('registration_email', e.target.value)} placeholder="register@example.com" />
              </Field>
              <Field label="Contact Email" sub="General contact form submissions">
                <input className="input-field text-sm" value={form.contact_email} onChange={e => set('contact_email', e.target.value)} placeholder="contact@example.com" />
              </Field>
              <Field label="Info Email" sub="General information inquiries displayed to visitors">
                <input className="input-field text-sm" value={form.info_email} onChange={e => set('info_email', e.target.value)} placeholder="info@example.com" />
              </Field>
              <Field label="Billing Email" sub="Billing-related communications">
                <input className="input-field text-sm" value={form.billing_email} onChange={e => set('billing_email', e.target.value)} placeholder="billing@example.com" />
              </Field>
              <Field label="Partner Email" sub="Partnership / business collaboration inquiries">
                <input className="input-field text-sm" value={form.partner_email} onChange={e => set('partner_email', e.target.value)} placeholder="partners@example.com" />
              </Field>
            </div>
          </Section>
          </>}

          {/* ═══════════════ TAB 2: Splash Page ═══════════════ */}
          {activeTab === 'splash' && <>
          {/* Splash Page */}
          <Section title="Splash Page">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div {...fieldProps('hero_headline')}>
                <Field label="Hero Headline" sub="Main heading on splash (default: auto from domain)">
                  <input className="input-field text-sm" value={form.hero_headline} onChange={e => set('hero_headline', e.target.value)} placeholder="Plan your next adventure" />
                </Field>
              </div>
              <div {...fieldProps('hero_subtitle')}>
                <Field label="Hero Subtitle" sub="Subtext below the hero heading">
                  <input className="input-field text-sm" value={form.hero_subtitle} onChange={e => set('hero_subtitle', e.target.value)} placeholder="Discover culture, not just destinations" />
                </Field>
              </div>
              <div {...fieldProps('cta_label')}>
                <Field label="CTA Button Label" sub="Primary call-to-action button text">
                  <input className="input-field text-sm" value={form.cta_label} onChange={e => set('cta_label', e.target.value)} placeholder="Start Traveling" />
                </Field>
              </div>
              <div {...fieldProps('company_name')}>
                <Field label="Company Name" sub="Footer copyright (default: Arrive Tech LLC)">
                  <input className="input-field text-sm" value={form.company_name} onChange={e => set('company_name', e.target.value)} placeholder="Arrive Tech LLC" />
                </Field>
              </div>
            </div>
            {/* Destinations settings moved to the Destinations tab */}
          </Section>

          {/* Trustpilot — per-tenant review widget on the splash hero.
              Optional. When Business Unit ID is blank, every Trustpilot
              CTA across the App hides on this tenant's domain (splash
              widget, save toast, journal-publish toast, Settings link,
              Sidebar link). White-label-safe. */}
          <Section title="Trustpilot">
            <p className="text-xs text-surface-500 -mt-2 mb-3">
              Show YOUR Trustpilot review widget on your splash hero and in-app review
              prompts. Leave Business Unit ID blank to hide every Trustpilot CTA across
              your app. Find your IDs in <a href="https://business.trustpilot.com" target="_blank" rel="noopener noreferrer" className="underline">Trustpilot Business</a> → Business unit settings.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div {...fieldProps('trustpilot_business_unit_id')}>
                <Field label="Business Unit ID" sub="24-char hex from Trustpilot. Required for any widget to render.">
                  <input
                    className="input-field text-sm font-mono"
                    value={form.trustpilot_business_unit_id}
                    onChange={e => set('trustpilot_business_unit_id', e.target.value.trim())}
                    placeholder="6a298da4262a8bb5d5eb17ec"
                  />
                </Field>
              </div>
              <div {...fieldProps('trustpilot_template_id')}>
                <Field label="Template ID (optional)" sub="Blank = Mini. See data-template-id on the embed code Trustpilot gives you.">
                  <input
                    className="input-field text-sm font-mono"
                    value={form.trustpilot_template_id}
                    onChange={e => set('trustpilot_template_id', e.target.value.trim())}
                    placeholder="53aa8807dec7e10d38f59f32"
                  />
                </Field>
              </div>
              <div className="sm:col-span-2" {...fieldProps('trustpilot_review_url')}>
                <Field label="Review URL (optional)" sub="Where 'Review us' links go. Blank = auto-derive from your tenant domain.">
                  <input
                    className="input-field text-sm"
                    value={form.trustpilot_review_url}
                    onChange={e => set('trustpilot_review_url', e.target.value.trim())}
                    placeholder="https://www.trustpilot.com/review/yourdomain.com"
                  />
                </Field>
              </div>
            </div>
          </Section>

          {/* About / influencer bio — surfaces a personal story section
              on the splash page so followers see WHO they're trusting
              with their travel, not just a generic tool. */}
          <Section title="About / Bio">
            <p className="text-xs text-surface-500 -mt-2 mb-3">
              Optional "About" section on your splash page. Leave blank to hide it. Especially useful for travel
              influencers, agencies, or destination experts.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div {...fieldProps('bio_heading')}>
                <Field label="Section Heading" sub="e.g. 'About Marisa'">
                  <input className="input-field text-sm" value={form.bio_heading} onChange={e => set('bio_heading', e.target.value)} placeholder="About me" />
                </Field>
              </div>
              <div {...fieldProps('bio_image_url')}>
                <Field label="Photo URL" sub="Square portrait works best">
                  <input className="input-field text-sm" value={form.bio_image_url} onChange={e => set('bio_image_url', e.target.value)} placeholder="https://..." />
                </Field>
              </div>
            </div>
            <div {...fieldProps('bio_text')}>
              <Field label="Bio" sub="2–4 sentences. Plain text, no HTML. Tip: ask the Branding Assistant (bottom-right) to draft this for you.">
                <textarea
                  rows={4}
                  className="input-field text-sm"
                  value={form.bio_text}
                  onChange={e => set('bio_text', e.target.value)}
                  placeholder="I've spent the last 8 years traveling 50+ countries. Here you'll find the trips I actually take, with all the cultural notes I wish I'd had on day one."
                />
              </Field>
            </div>
          </Section>

          {/* Curated Trips — the "Steal My Itinerary" section on splash.
              Renders any trip the user has flagged as a public template. */}
          <Section title="Curated Trips">
            <p className="text-xs text-surface-500 -mt-2 mb-3">
              The "Steal My Itinerary" section on your splash. Any trip you mark
              as a public template appears here so visitors can copy it into
              their own account in one tap. Hides automatically when no
              templates exist, even if the toggle is on.
            </p>

            {/* Show / hide toggle */}
            <div className="flex items-center justify-between p-3 rounded-lg border border-surface-700/50">
              <div>
                <span className="text-sm font-medium text-surface-800">Show on splash</span>
                <p className="text-xs text-surface-500">Display your trip templates publicly</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={form.show_curated_trips}
                onClick={() => set('show_curated_trips', !form.show_curated_trips)}
                className={cn(
                  'w-10 h-5 rounded-full transition-colors relative shrink-0',
                  form.show_curated_trips ? 'bg-emerald-500' : 'bg-slate-400'
                )}
              >
                <div className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200"
                  style={{ transform: form.show_curated_trips ? 'translateX(20px)' : 'translateX(0)' }} />
              </button>
            </div>

            {form.show_curated_trips && (
              <div className="space-y-3 mt-3">
                <div {...fieldProps('curated_trips_heading')}>
                  <Field label="Section Heading" sub="Default: Steal My Itinerary">
                    <input
                      className="input-field text-sm"
                      value={form.curated_trips_heading}
                      onChange={e => set('curated_trips_heading', e.target.value)}
                      placeholder="Steal My Itinerary"
                    />
                  </Field>
                </div>
                <div {...fieldProps('curated_trips_subtitle')}>
                  <Field label="Section Subtitle" sub="One short line below the heading">
                    <input
                      className="input-field text-sm"
                      value={form.curated_trips_subtitle}
                      onChange={e => set('curated_trips_subtitle', e.target.value)}
                      placeholder="Real trips I've planned. Copy any one into your account in a click — then make it your own."
                    />
                  </Field>
                </div>
                <div {...fieldProps('curated_trips_layout')}>
                  <label className="block text-sm font-medium text-surface-700 mb-2">Layout</label>
                  <div className="flex gap-2">
                    {CURATED_TRIPS_LAYOUTS.map(layout => (
                      <button
                        key={layout.value}
                        type="button"
                        onClick={() => set('curated_trips_layout', layout.value)}
                        className={cn(
                          'flex-1 flex flex-col items-center gap-1 py-2.5 px-2 rounded-lg border text-center transition-all',
                          form.curated_trips_layout === layout.value
                            ? 'border-emerald-500/50 badge-emerald'
                            : 'border-surface-700 text-surface-500 hover:border-surface-500'
                        )}
                      >
                        <span className="text-lg">{layout.emoji}</span>
                        <span className="text-xs font-medium">{layout.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </Section>

          {/* Content Feeds — Blog / YouTube / Instagram */}
          <Section title="Content Feeds">
            <p className="text-xs text-surface-500 -mt-2 mb-3">Pull in content from your blog, YouTube channel, or Instagram to showcase on your splash page.</p>

            {/* Latest Tab — items per type */}
            {(form.show_blog_feed || form.show_youtube_feed || form.show_instagram_feed) && (
              <div className="flex items-center justify-between p-3 rounded-lg border border-surface-700/50 mb-1">
                <div>
                  <span className="text-sm font-medium text-surface-800">Latest Tab</span>
                  <p className="text-xs text-surface-500">Items shown per content type in the Latest tab</p>
                </div>
                <input type="number" min={1} max={10}
                  className="input-field text-sm w-16 text-center"
                  value={form.latest_feed_count}
                  onChange={e => set('latest_feed_count', Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                />
              </div>
            )}

            {/* Blog Feed */}
            <div className="space-y-3 p-3 rounded-lg border border-surface-700/50">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-surface-800">Blog Feed</span>
                  <p className="text-xs text-surface-500">WordPress, Substack, or any RSS feed</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={form.show_blog_feed}
                  onClick={() => set('show_blog_feed', !form.show_blog_feed)}
                  className={cn(
                    'w-10 h-5 rounded-full transition-colors relative shrink-0',
                    form.show_blog_feed ? 'bg-emerald-500' : 'bg-slate-400'
                  )}
                >
                  <div className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200"
                    style={{ transform: form.show_blog_feed ? 'translateX(20px)' : 'translateX(0)' }} />
                </button>
              </div>
              {form.show_blog_feed && (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    {(['wordpress', 'substack', 'rss'] as const).map(t => (
                      <button key={t} onClick={() => set('blog_source_type', t)}
                        className={cn(
                          'px-3 py-1.5 text-xs font-medium rounded-lg border transition-all capitalize',
                          form.blog_source_type === t
                            ? 'border-emerald-500/50 badge-emerald'
                            : 'border-surface-700 text-surface-500 hover:border-surface-500'
                        )}
                      >
                        {t === 'rss' ? 'RSS Feed' : t}
                      </button>
                    ))}
                  </div>
                  <input
                    className="input-field text-sm"
                    value={form.blog_source_url || ''}
                    onChange={e => set('blog_source_url', e.target.value)}
                    placeholder={form.blog_source_type === 'wordpress' ? 'https://yourblog.com' : form.blog_source_type === 'substack' ? 'https://yourname.substack.com' : 'https://yourblog.com/feed'}
                  />
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <label className="block text-xs text-surface-500 mb-1">Layout</label>
                      <div className="flex gap-1.5">
                        {BLOG_FEED_LAYOUTS.map(layout => (
                          <button key={layout.value} onClick={() => set('blog_feed_layout', layout.value)}
                            className={cn('flex-1 flex flex-col items-center gap-0.5 py-1.5 px-1 rounded-lg border text-center transition-all',
                              form.blog_feed_layout === layout.value ? 'border-emerald-500/50 badge-emerald' : 'border-surface-700 text-surface-500 hover:border-surface-500'
                            )}>
                            <span className="text-sm">{layout.emoji}</span>
                            <span className="text-[10px] font-medium">{layout.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="w-16">
                      <label className="block text-xs text-surface-500 mb-1">Count</label>
                      <input type="number" min={1} max={20}
                        className="input-field text-sm w-full text-center"
                        value={form.blog_feed_count}
                        onChange={e => set('blog_feed_count', Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* YouTube Feed */}
            <div className="space-y-3 p-3 rounded-lg border border-surface-700/50">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-surface-800">YouTube Videos</span>
                  <p className="text-xs text-surface-500">Show latest videos from your channel</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={form.show_youtube_feed}
                  onClick={() => set('show_youtube_feed', !form.show_youtube_feed)}
                  className={cn(
                    'w-10 h-5 rounded-full transition-colors relative shrink-0',
                    form.show_youtube_feed ? 'bg-emerald-500' : 'bg-slate-400'
                  )}
                >
                  <div className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200"
                    style={{ transform: form.show_youtube_feed ? 'translateX(20px)' : 'translateX(0)' }} />
                </button>
              </div>
              {form.show_youtube_feed && (
                <div className="space-y-2">
                  <input
                    className="input-field text-sm"
                    value={form.youtube_handle || ''}
                    onChange={e => set('youtube_handle', e.target.value)}
                    placeholder="@YourChannelHandle"
                  />
                  <p className="text-[10px] text-surface-500">Or enter Channel ID directly:</p>
                  <input
                    className="input-field text-sm"
                    value={form.youtube_channel_id || ''}
                    onChange={e => set('youtube_channel_id', e.target.value)}
                    placeholder="UCxxxxxxxxxxxxxx"
                  />
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <label className="block text-xs text-surface-500 mb-1">Layout</label>
                      <div className="flex gap-1.5">
                        {YOUTUBE_FEED_LAYOUTS.map(layout => (
                          <button key={layout.value} onClick={() => set('youtube_feed_layout', layout.value)}
                            className={cn('flex-1 flex flex-col items-center gap-0.5 py-1.5 px-1 rounded-lg border text-center transition-all',
                              form.youtube_feed_layout === layout.value ? 'border-emerald-500/50 badge-emerald' : 'border-surface-700 text-surface-500 hover:border-surface-500'
                            )}>
                            <span className="text-sm">{layout.emoji}</span>
                            <span className="text-[10px] font-medium">{layout.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="w-16">
                      <label className="block text-xs text-surface-500 mb-1">Count</label>
                      <input type="number" min={1} max={20}
                        className="input-field text-sm w-full text-center"
                        value={form.youtube_feed_count}
                        onChange={e => set('youtube_feed_count', Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Instagram Feed */}
            <div className="space-y-3 p-3 rounded-lg border border-surface-700/50">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-surface-800">Instagram Feed</span>
                  <p className="text-xs text-surface-500">Display your latest Instagram posts</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={form.show_instagram_feed}
                  onClick={() => set('show_instagram_feed', !form.show_instagram_feed)}
                  className={cn(
                    'w-10 h-5 rounded-full transition-colors relative shrink-0',
                    form.show_instagram_feed ? 'bg-emerald-500' : 'bg-slate-400'
                  )}
                >
                  <div className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200"
                    style={{ transform: form.show_instagram_feed ? 'translateX(20px)' : 'translateX(0)' }} />
                </button>
              </div>
              {form.show_instagram_feed && (
                <div className="space-y-2">
                  <input
                    className="input-field text-sm"
                    value={form.instagram_handle || ''}
                    onChange={e => set('instagram_handle', e.target.value)}
                    placeholder="@yourusername"
                  />
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <label className="block text-xs text-surface-500 mb-1">Layout</label>
                      <div className="flex gap-1.5">
                        {INSTAGRAM_FEED_LAYOUTS.map(layout => (
                          <button key={layout.value} onClick={() => set('instagram_feed_layout', layout.value)}
                            className={cn('flex-1 flex flex-col items-center gap-0.5 py-1.5 px-1 rounded-lg border text-center transition-all',
                              form.instagram_feed_layout === layout.value ? 'border-emerald-500/50 badge-emerald' : 'border-surface-700 text-surface-500 hover:border-surface-500'
                            )}>
                            <span className="text-sm">{layout.emoji}</span>
                            <span className="text-[10px] font-medium">{layout.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="w-16">
                      <label className="block text-xs text-surface-500 mb-1">Count</label>
                      <input type="number" min={1} max={20}
                        className="input-field text-sm w-full text-center"
                        value={form.instagram_feed_count}
                        onChange={e => set('instagram_feed_count', Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* TikTok Feed — manual list of video URLs (oEmbed-based) ──── */}
            <div className="space-y-3 p-3 rounded-lg border border-surface-700/50">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-surface-800">TikTok Feed</span>
                  <p className="text-xs text-surface-500">Paste your best TikTok video URLs to feature on the splash</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={form.show_tiktok_feed}
                  onClick={() => set('show_tiktok_feed', !form.show_tiktok_feed)}
                  className={cn(
                    'w-10 h-5 rounded-full transition-colors relative shrink-0',
                    form.show_tiktok_feed ? 'bg-emerald-500' : 'bg-slate-400'
                  )}
                >
                  <div className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200"
                    style={{ transform: form.show_tiktok_feed ? 'translateX(20px)' : 'translateX(0)' }} />
                </button>
              </div>
              {form.show_tiktok_feed && (
                <div className="space-y-2">
                  <input
                    className="input-field text-sm"
                    value={form.tiktok_handle || ''}
                    onChange={e => set('tiktok_handle', e.target.value)}
                    placeholder="@yourusername (optional — used for the 'Follow' link)"
                  />

                  {/* URL list editor — one URL per row, add/remove inline */}
                  <div className="space-y-1.5">
                    <label className="block text-xs text-surface-500">
                      Video URLs <span className="text-surface-400">({(form.tiktok_video_urls || []).length}/9)</span>
                    </label>
                    {(form.tiktok_video_urls || []).map((url: string, i: number) => (
                      <div key={i} className="flex gap-1.5">
                        <input
                          className="input-field text-sm flex-1"
                          value={url}
                          onChange={e => {
                            const next = [...(form.tiktok_video_urls || [])];
                            next[i] = e.target.value;
                            set('tiktok_video_urls', next);
                          }}
                          placeholder="https://www.tiktok.com/@user/video/1234567890"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const next = (form.tiktok_video_urls || []).filter((_: string, j: number) => j !== i);
                            set('tiktok_video_urls', next);
                          }}
                          className="px-2 rounded-lg border border-surface-700 text-surface-500 hover:text-red-400 hover:border-red-700/50 text-xs"
                          title="Remove this video"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    {(form.tiktok_video_urls || []).length < 9 && (
                      <button
                        type="button"
                        onClick={() => set('tiktok_video_urls', [...(form.tiktok_video_urls || []), ''])}
                        className="text-xs text-emerald-500 hover:text-emerald-400 font-medium"
                      >
                        + Add a TikTok video URL
                      </button>
                    )}
                    <p className="text-xs text-surface-500 pt-1">
                      On TikTok, tap the <strong>Share</strong> icon on a video → <strong>Copy link</strong>. Paste the URL here. We pull thumbnail + author automatically via TikTok's oEmbed (no auth, no tokens).
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <div className="flex-1">
                      <label className="block text-xs text-surface-500 mb-1">Layout</label>
                      <div className="flex gap-1.5">
                        {TIKTOK_FEED_LAYOUTS.map(layout => (
                          <button key={layout.value} onClick={() => set('tiktok_feed_layout', layout.value)}
                            className={cn('flex-1 flex flex-col items-center gap-0.5 py-1.5 px-1 rounded-lg border text-center transition-all',
                              form.tiktok_feed_layout === layout.value ? 'border-emerald-500/50 badge-emerald' : 'border-surface-700 text-surface-500 hover:border-surface-500'
                            )}>
                            <span className="text-sm">{layout.emoji}</span>
                            <span className="text-[10px] font-medium">{layout.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="w-16">
                      <label className="block text-xs text-surface-500 mb-1">Count</label>
                      <input type="number" min={1} max={9}
                        className="input-field text-sm w-full text-center"
                        value={form.tiktok_feed_count}
                        onChange={e => set('tiktok_feed_count', Math.max(1, Math.min(9, parseInt(e.target.value) || 1)))}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Section>

          </>}

          {/* ═══════════════ TAB 3: Destinations ═══════════════ */}
          {activeTab === 'destinations' && (
            <ShowcaseDestinationsTab
              tenantId={tenantId}
              showDestinations={form.show_destinations}
              destinationsLayout={form.destinations_layout}
              useCustomDestinations={form.use_custom_destinations}
              customDestinationsLayout={form.custom_destinations_layout}
              onFieldChange={set}
            />
          )}

          {/* ═══════════════ TAB 4: Cards ═══════════════ */}
          {activeTab === 'cards' && <>

          {/* Feature Cards */}
          <Section title="Feature Cards">
            <Field label="Section Heading" sub="Heading above feature cards (default: Everything you need to travel smarter)">
              <input className="input-field text-sm" value={form.feature_cards_heading} onChange={e => set('feature_cards_heading', e.target.value)} placeholder="Everything you need to travel smarter" />
            </Field>
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-2">Card Layout</label>
              <div className="flex gap-2">
                {FEATURE_CARD_LAYOUTS.map(layout => (
                  <button
                    key={layout.value}
                    onClick={() => set('feature_cards_layout', layout.value)}
                    className={cn(
                      'flex-1 flex flex-col items-center gap-1 py-2.5 px-2 rounded-lg border text-center transition-all',
                      form.feature_cards_layout === layout.value
                        ? 'border-emerald-500/50 badge-emerald'
                        : 'border-surface-700 text-surface-500 hover:border-surface-500'
                    )}
                  >
                    <span className="text-lg">{layout.emoji}</span>
                    <span className="text-xs font-medium">{layout.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-1">Select Features to Display</label>
              <p className="text-xs text-surface-500 mb-3">Choose which feature cards to show on your splash page. Only features enabled in your plan are available.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {FEATURE_CARD_OPTIONS.map((card) => {
                  const featureEnabled = planFeatures?.[card.slug] === true;
                  const isSelected = form.feature_cards.includes(card.slug);
                  const CardIcon = card.icon;
                  return (
                    <label
                      key={card.slug}
                      className={cn(
                        'flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all',
                        !featureEnabled && 'opacity-40 cursor-not-allowed',
                        isSelected ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-surface-700 hover:border-surface-500',
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={!featureEnabled}
                        onChange={() => {
                          if (!featureEnabled) return;
                          set('feature_cards',
                            isSelected
                              ? form.feature_cards.filter((s: string) => s !== card.slug)
                              : [...form.feature_cards, card.slug]
                          );
                        }}
                        className="rounded border-surface-600 text-emerald-500 focus:ring-emerald-500"
                      />
                      <CardIcon className="w-4 h-4 text-emerald-400 shrink-0" />
                      <div className="min-w-0">
                        <span className="text-sm font-medium text-surface-800 block truncate">{card.title}</span>
                        <span className="text-xs text-surface-500 block truncate">{card.description}</span>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          </Section>

          {/* Info Cards */}
          <Section title="Info Cards">
            <Field label="Section Heading" sub="Heading above info cards (default: Cultural Insights)">
              <input className="input-field text-sm" value={form.info_cards_heading} onChange={e => set('info_cards_heading', e.target.value)} placeholder="Cultural Insights" />
            </Field>
            <Field label="Section Subtitle" sub="Subtitle below the heading">
              <input className="input-field text-sm" value={form.info_cards_subtitle} onChange={e => set('info_cards_subtitle', e.target.value)} placeholder="Travel respectfully with essential tips" />
            </Field>
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-2">Card Layout</label>
              <div className="flex gap-2">
                {INFO_CARD_LAYOUTS.map(layout => (
                  <button
                    key={layout.value}
                    onClick={() => set('info_cards_layout', layout.value)}
                    className={cn(
                      'flex-1 flex flex-col items-center gap-1 py-2.5 px-2 rounded-lg border text-center transition-all',
                      form.info_cards_layout === layout.value
                        ? 'border-emerald-500/50 badge-emerald'
                        : 'border-surface-700 text-surface-500 hover:border-surface-500'
                    )}
                  >
                    <span className="text-lg">{layout.emoji}</span>
                    <span className="text-xs font-medium">{layout.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-2">Card Mode</label>
              <div className="flex gap-2">
                {(['preset', 'custom'] as const).map(mode => (
                  <button
                    key={mode}
                    onClick={() => set('info_cards_mode', mode)}
                    className={cn(
                      'px-4 py-2 text-sm font-medium rounded-lg border transition-all',
                      form.info_cards_mode === mode
                        ? 'border-emerald-500/50 badge-emerald'
                        : 'border-surface-700 text-surface-500 hover:border-surface-500'
                    )}
                  >
                    {mode === 'preset' ? 'Preset Categories' : 'Custom Cards'}
                  </button>
                ))}
              </div>
            </div>

            {form.info_cards_mode === 'preset' ? (
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-1">Select Categories</label>
                <p className="text-xs text-surface-500 mb-3">Pick which cultural tip categories to show (default: first 6)</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {INFO_CARD_PRESETS.map((preset) => {
                    const isSelected = form.info_cards_presets.includes(preset.slug);
                    const PresetIcon = preset.icon;
                    return (
                      <button
                        key={preset.slug}
                        type="button"
                        onClick={() => {
                          set('info_cards_presets',
                            isSelected
                              ? form.info_cards_presets.filter((s: string) => s !== preset.slug)
                              : [...form.info_cards_presets, preset.slug]
                          );
                        }}
                        className={cn(
                          'flex items-start gap-3 p-3.5 rounded-xl border text-left transition-all group',
                          isSelected
                            ? 'border-emerald-500/50 bg-emerald-500/5 ring-1 ring-emerald-500/20'
                            : 'border-surface-700 hover:border-surface-500 hover:bg-surface-800/30',
                        )}
                      >
                        <div className={cn(
                          'w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-colors',
                          isSelected ? 'bg-emerald-500 shadow-md shadow-emerald-500/20' : 'bg-surface-800 group-hover:bg-surface-700',
                        )}>
                          <PresetIcon className={cn('w-5 h-5', isSelected ? 'text-white' : 'text-surface-400')} />
                        </div>
                        <div className="min-w-0">
                          <div className={cn('text-sm font-medium leading-tight', isSelected ? 'text-emerald-400' : 'text-surface-800')}>
                            {preset.title}
                          </div>
                          <div className="text-xs text-surface-500 mt-0.5 leading-snug">{preset.description}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-1">Custom Cards</label>
                <p className="text-xs text-surface-500 mb-3">Add up to 6 custom cards with icon, title and description</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {form.info_cards_custom.map((card: { title: string; description: string; icon?: string }, i: number) => {
                    const selectedIcon = INFO_CARD_ICONS.find(ic => ic.name === card.icon);
                    const IconComp = selectedIcon?.component;
                    return (
                      <div
                        key={i}
                        className="flex items-start gap-3 p-3.5 rounded-xl border border-emerald-500/50 bg-emerald-500/5 ring-1 ring-emerald-500/20 relative group"
                      >
                        {/* Icon picker */}
                        <div className="shrink-0 relative">
                          <button
                            type="button"
                            className="w-10 h-10 rounded-xl bg-emerald-500 shadow-md shadow-emerald-500/20 flex items-center justify-center hover:bg-emerald-400 transition-colors"
                            onClick={() => {
                              const el = document.getElementById(`icon-picker-${i}`);
                              if (el) el.classList.toggle('hidden');
                            }}
                            title="Choose icon"
                          >
                            {IconComp ? <IconComp className="w-5 h-5 text-white" /> : <Star className="w-5 h-5 text-white" />}
                          </button>
                          {/* Icon picker dropdown */}
                          <div id={`icon-picker-${i}`} className="hidden absolute top-12 left-0 z-20 w-[240px] max-h-[200px] overflow-y-auto bg-surface-900 border border-surface-600 rounded-xl shadow-2xl p-2.5 grid grid-cols-6 gap-1.5">
                            {INFO_CARD_ICONS.map(ic => {
                              const Ic = ic.component;
                              return (
                                <button
                                  key={ic.name}
                                  type="button"
                                  onClick={() => {
                                    const updated = [...form.info_cards_custom];
                                    updated[i] = { ...updated[i], icon: ic.name };
                                    set('info_cards_custom', updated);
                                    document.getElementById(`icon-picker-${i}`)?.classList.add('hidden');
                                  }}
                                  className={cn(
                                    'w-8 h-8 rounded-lg flex items-center justify-center transition-all',
                                    card.icon === ic.name ? 'bg-emerald-500 text-white shadow-sm' : 'text-surface-400 hover:bg-surface-700 hover:text-white'
                                  )}
                                  title={ic.label}
                                >
                                  <Ic className="w-4 h-4" />
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0 space-y-1.5">
                          <input
                            className="w-full bg-transparent border-0 border-b border-surface-700 focus:border-emerald-500 text-sm font-medium text-emerald-400 placeholder:text-surface-500 px-0 py-0.5 focus:ring-0 focus:outline-none"
                            value={card.title}
                            onChange={e => {
                              const updated = [...form.info_cards_custom];
                              updated[i] = { ...updated[i], title: e.target.value };
                              set('info_cards_custom', updated);
                            }}
                            placeholder="Card title"
                          />
                          <input
                            className="w-full bg-transparent border-0 border-b border-transparent focus:border-surface-700 text-xs text-surface-500 placeholder:text-surface-600 px-0 py-0.5 focus:ring-0 focus:outline-none"
                            value={card.description}
                            onChange={e => {
                              const updated = [...form.info_cards_custom];
                              updated[i] = { ...updated[i], description: e.target.value };
                              set('info_cards_custom', updated);
                            }}
                            placeholder="Short description"
                          />
                        </div>
                        {/* Remove button */}
                        <button
                          onClick={() => set('info_cards_custom', form.info_cards_custom.filter((_: any, j: number) => j !== i))}
                          className="absolute top-2 right-2 w-5 h-5 rounded-full bg-surface-800 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-surface-500 hover:text-red-600 dark:text-red-400 hover:bg-red-500/10"
                          title="Remove card"
                        >
                          <XIcon className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                  {form.info_cards_custom.length < 6 && (
                    <button
                      onClick={() => set('info_cards_custom', [...form.info_cards_custom, { title: '', description: '', icon: 'star' }])}
                      className={cn(
                        'flex items-center justify-center gap-2 p-3.5 rounded-xl border border-dashed transition-all',
                        'border-surface-700 hover:border-emerald-500/50 hover:bg-emerald-500/5 text-surface-500 hover:text-emerald-400'
                      )}
                    >
                      <span className="text-lg leading-none">+</span>
                      <span className="text-sm font-medium">Add Card</span>
                    </button>
                  )}
                </div>
              </div>
            )}
          </Section>

          </>}

          {/* ═══════════════ TAB: Smart Planning ═══════════════ */}
          {activeTab === 'smart-planning' && <>
          <Section title="Page Settings">
            <Field label="Page Heading" sub="The main heading shown at the top of the itinerary results page">
              <input
                className="input-field text-sm"
                value={form.itinerary_page_heading}
                onChange={e => set('itinerary_page_heading', e.target.value)}
                placeholder="Your Culturally Affluent Plan"
              />
            </Field>
            <div className="flex items-center justify-between py-1">
              <div>
                <p className="text-sm font-medium text-surface-700">Section Navigation Pills</p>
                <p className="text-xs text-surface-500">Show sticky pill bar for quick section navigation</p>
              </div>
              <button
                onClick={() => set('itinerary_show_pills', !form.itinerary_show_pills)}
                className={cn(
                  'relative w-10 h-5.5 rounded-full transition-colors',
                  form.itinerary_show_pills ? 'bg-emerald-500' : 'bg-surface-600'
                )}
              >
                <span className={cn(
                  'absolute top-0.5 left-0.5 w-4.5 h-4.5 rounded-full bg-white transition-transform shadow-sm',
                  form.itinerary_show_pills && 'translate-x-[18px]'
                )} />
              </button>
            </div>
            <div className="flex items-center justify-between py-1">
              <div>
                <p className="text-sm font-medium text-surface-700">City Images</p>
                <p className="text-xs text-surface-500">Show destination photos in the overview section</p>
              </div>
              <button
                onClick={() => set('itinerary_show_city_images', !form.itinerary_show_city_images)}
                className={cn(
                  'relative w-10 h-5.5 rounded-full transition-colors',
                  form.itinerary_show_city_images ? 'bg-emerald-500' : 'bg-surface-600'
                )}
              >
                <span className={cn(
                  'absolute top-0.5 left-0.5 w-4.5 h-4.5 rounded-full bg-white transition-transform shadow-sm',
                  form.itinerary_show_city_images && 'translate-x-[18px]'
                )} />
              </button>
            </div>
          </Section>

          <Section title="Sections">
            <p className="text-xs text-surface-500 -mt-2 mb-3">
              Drag to reorder, toggle visibility, and set custom headings for each section.
              Overview and Itinerary are always visible.
            </p>
            <DndContext
              sensors={dndSensors}
              collisionDetection={closestCenter}
              onDragEnd={(event: DragEndEvent) => {
                const { active, over } = event;
                if (over && active.id !== over.id) {
                  const oldIndex = form.itinerary_sections.findIndex(s => s.id === active.id);
                  const newIndex = form.itinerary_sections.findIndex(s => s.id === over.id);
                  set('itinerary_sections', arrayMove(form.itinerary_sections, oldIndex, newIndex));
                }
              }}
            >
              {/* Filter out legacy section ids (e.g. 'download') that no
                  longer have a Results-page render block. We never delete
                  them from the saved config — they just stay dormant. */}
              <SortableContext
                items={form.itinerary_sections.filter(s => !LEGACY_SECTION_IDS.has(s.id)).map(s => s.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-1.5">
                  {form.itinerary_sections.filter(s => !LEGACY_SECTION_IDS.has(s.id)).map((section) => (
                    <SortableSectionRow
                      key={section.id}
                      section={section}
                      meta={SECTION_DEFAULTS[section.id]}
                      onToggle={() => {
                        if (SECTION_DEFAULTS[section.id]?.locked) return;
                        set('itinerary_sections', form.itinerary_sections.map(s =>
                          s.id === section.id ? { ...s, visible: !s.visible } : s
                        ));
                      }}
                      onLabelChange={(label: string) => {
                        set('itinerary_sections', form.itinerary_sections.map(s =>
                          s.id === section.id ? { ...s, label } : s
                        ));
                      }}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
            <button
              onClick={() => set('itinerary_sections', [...DEFAULT_ITINERARY_SECTIONS])}
              className="mt-3 text-xs text-surface-500 hover:text-emerald-400 transition-colors"
            >
              Reset to Default Order
            </button>
          </Section>
          </>}

          {/* Social Links — in Emails & Social tab */}
          {activeTab === 'emails' && (
          <Section title="Social Links">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Facebook URL"><input className="input-field text-sm" value={form.facebook_url} onChange={e => set('facebook_url', e.target.value)} placeholder="https://facebook.com/..." /></Field>
              <Field label="Instagram URL"><input className="input-field text-sm" value={form.instagram_url} onChange={e => set('instagram_url', e.target.value)} placeholder="https://instagram.com/..." /></Field>
              <Field label="X (Twitter) URL"><input className="input-field text-sm" value={form.x_url} onChange={e => set('x_url', e.target.value)} placeholder="https://x.com/..." /></Field>
              <Field label="YouTube URL"><input className="input-field text-sm" value={form.youtube_url} onChange={e => set('youtube_url', e.target.value)} placeholder="https://youtube.com/..." /></Field>
              <Field label="LinkedIn URL"><input className="input-field text-sm" value={form.linkedin_url} onChange={e => set('linkedin_url', e.target.value)} placeholder="https://linkedin.com/..." /></Field>
            </div>
          </Section>
          )}

          {/* ═══════════════ TAB 5: SEO & Advanced ═══════════════ */}
          {activeTab === 'seo' && <>
          <Section title="SEO">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Meta Title"><input className="input-field text-sm" value={form.meta_title} onChange={e => set('meta_title', e.target.value)} placeholder="My App — Trip Planner" /></Field>
              <Field label="Meta Description"><input className="input-field text-sm" value={form.meta_description} onChange={e => set('meta_description', e.target.value)} placeholder="Plan trips with ease" /></Field>
            </div>
          </Section>
          <Section title="Custom CSS (Advanced)">
            <Field label="CSS Overrides" sub="Applied after default styles">
              <textarea className="input-field font-mono text-xs h-28 resize-y" value={form.custom_css}
                onChange={e => set('custom_css', e.target.value)} placeholder=":root { --custom-var: #fff; }" />
            </Field>
          </Section>
          </>}

        </div>

        {/* ── Live Preview (right) ───────────────────────────────────────── */}
        <div className="xl:col-span-2">
          <div className="sticky top-6">
            <div className="glass-panel p-5">
              <h3 className="text-sm font-semibold text-surface-900 mb-4">Live Preview</h3>
              <PreviewTabs form={form} features={planFeatures as any} />
              <p className="text-[10px] text-surface-500 mt-3 text-center italic leading-snug">
                This preview illustrates how your brand colours will appear in the app.
                Actual rendering may vary slightly depending on the user's device and theme settings.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Branding Assistant Chatbot */}
      <BrandingAssistant form={form} set={set} onFieldApplied={onFieldApplied} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* COLOR UTILITIES                                                            */
/* ═══════════════════════════════════════════════════════════════════════════ */

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  if (h.length !== 6) return [13, 148, 136]; // fallback teal
  const n = parseInt(h, 16);
  if (isNaN(n)) return [13, 148, 136];
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map(v => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');
}

function lightenColor(hex: string, amount: number): string {
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex(
    r + (255 - r) * amount,
    g + (255 - g) * amount,
    b + (255 - b) * amount,
  );
}

function hexAlpha(hex: string, alpha: number): string {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r},${g},${b},${alpha})`;
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* INLINE SVG ICONS (Lucide paths)                                            */
/* ═══════════════════════════════════════════════════════════════════════════ */

const I = ({ d, color = 'currentColor', size = 16, fill }: { d: string; color?: string; size?: number; fill?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill || 'none'} stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

const IconBell = ({ color = 'currentColor', size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </svg>
);

const IconMapPin = ({ color = 'currentColor', size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
  </svg>
);

const IconSparkles = ({ color = 'currentColor', size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
    <path d="M5 3v4" /><path d="M19 17v4" /><path d="M3 5h4" /><path d="M17 19h4" />
  </svg>
);

const IconBookOpen = ({ color = 'currentColor', size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
  </svg>
);

const IconCompass = ({ color = 'currentColor', size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
  </svg>
);

const IconVideo = ({ color = 'currentColor', size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m22 8-6 4 6 4V8Z" /><rect width="14" height="12" x="2" y="6" rx="2" ry="2" />
  </svg>
);

const IconCrown = ({ color = 'currentColor', size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m2 4 3 12h14l3-12-6 7-4-7-4 7-6-7Z" /><path d="M5 20h14" />
  </svg>
);

const IconHome = ({ color = 'currentColor', size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

const IconGlobe = ({ color = 'currentColor', size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" /><path d="M2 12h20" />
  </svg>
);

const IconBookmark = ({ color = 'currentColor', size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z" />
  </svg>
);

/* ═══════════════════════════════════════════════════════════════════════════ */
/* LIVE PREVIEW — Realistic B2C HomeScreen                                    */
/* ═══════════════════════════════════════════════════════════════════════════ */

function LivePreview({ form, features, mode = 'dark' }: { form: Record<string, string>; features?: FeatureConfig; mode?: 'dark' | 'light' }) {
  const isDark = mode === 'dark';
  const colors = useMemo(() => {
    const primary = form.primary_color || '#0d9488';
    const secondary = form.secondary_color || lightenColor(primary, 0.3);
    return {
      primary,
      secondary,
      primaryFaint: hexAlpha(primary, 0.10),
      primaryBorder: hexAlpha(primary, 0.30),
      secondaryFaint: hexAlpha(secondary, 0.10),
      bg: isDark ? '#1a1a1a' : '#faf8f6',
      cardBg: isDark ? '#292929' : '#ffffff',
      cardBorder: isDark ? '#3a3a3a' : '#e8e2db',
      text: isDark ? '#f2f2f2' : '#292929',
      textMuted: isDark ? '#999999' : '#737373',
      textDim: isDark ? '#666666' : '#999999',
    };
  }, [form.primary_color, form.secondary_color, isDark]);

  const fontFamily = form.font_family || 'DM Sans, sans-serif';
  const appName = form.app_name || 'DiscoveryGem';
  const showCultural = features?.cultural_hub !== false;
  const showZoom = features?.zoom_meetings !== false;
  const showAi = features?.ai_trip_builder !== false;

  return (
    <div style={{
      width: '100%', maxWidth: 320, margin: '0 auto',
      borderRadius: 40, border: `8px solid ${isDark ? '#1c1c1c' : '#d1d5db'}`,
      backgroundColor: isDark ? '#000' : '#e5e7eb',
      boxShadow: isDark ? '0 25px 60px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05)' : '0 25px 60px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.06)',
      overflow: 'hidden', fontFamily,
      display: 'flex', flexDirection: 'column' as const,
    }}>
      {/* ── Status Bar ──────────────────────────────────────────────────── */}
      <div style={{
        height: 44, backgroundColor: colors.bg, display: 'flex',
        alignItems: 'center', justifyContent: 'space-between',
        padding: '0 24px', position: 'relative' as const,
      }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: colors.text }}>9:41</span>
        <div style={{
          position: 'absolute' as const, top: 0, left: '50%', transform: 'translateX(-50%)',
          width: 100, height: 26, backgroundColor: '#000', borderRadius: '0 0 16px 16px',
        }} />
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 1.5 }}>
            {[10, 12, 14, 16].map((h) => (
              <div key={h} style={{ width: 3, height: h, borderRadius: 1, backgroundColor: colors.text, opacity: 0.6 }} />
            ))}
          </div>
          <I d="M22 11v1a10 10 0 1 1-4-8" color={colors.text} size={11} />
          <div style={{ width: 20, height: 9, borderRadius: 2, border: `1px solid ${hexAlpha(colors.text, 0.4)}`, position: 'relative' as const, marginLeft: 2 }}>
            <div style={{ position: 'absolute' as const, top: 1, left: 1, bottom: 1, width: '70%', borderRadius: 1, backgroundColor: colors.text, opacity: 0.6 }} />
          </div>
        </div>
      </div>

      {/* ── Scrollable Content ──────────────────────────────────────────── */}
      <div style={{
        height: 550, overflowY: 'auto' as const, backgroundColor: colors.bg,
        padding: '12px 16px 16px',
        scrollbarWidth: 'none' as const,
      }}>

        {/* App name badge + bell */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '4px 10px', backgroundColor: colors.primaryFaint,
            border: `1px solid ${colors.primaryBorder}`, borderRadius: 99,
            fontSize: 10, fontWeight: 600, color: colors.primary,
          }}>
            {form.logo_url ? (
              <img src={form.logo_url} alt="" style={{ width: 14, height: 14, borderRadius: 99, objectFit: 'contain' as const }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            ) : null}
            {appName}
          </div>
          <div style={{
            width: 32, height: 32, borderRadius: 99,
            backgroundColor: colors.cardBg, border: `1px solid ${colors.cardBorder}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <IconBell color={colors.secondary} size={14} />
          </div>
        </div>

        {/* Welcome, Sarah */}
        <div style={{ marginBottom: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
            <span style={{ fontSize: 18, fontWeight: 700, color: colors.text }}>Welcome, Sarah</span>
            <span style={{
              fontSize: 9, fontWeight: 500, color: colors.textMuted,
              backgroundColor: colors.cardBg, border: `1px solid ${colors.cardBorder}`,
              padding: '2px 7px', borderRadius: 99,
            }}>Explorer Free</span>
          </div>
          <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 10 }}>
            {form.welcome_message || 'Travel with cultural clarity'}
          </div>
        </div>

        {/* Quick chips: Plan, Explore */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '5px 10px', backgroundColor: colors.cardBg,
            border: `1px solid ${colors.cardBorder}`, borderRadius: 99,
            fontSize: 10, fontWeight: 500, color: colors.text,
          }}>
            <IconMapPin color={colors.primary} size={11} /> Plan
          </div>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '5px 10px', backgroundColor: colors.cardBg,
            border: `1px solid ${colors.cardBorder}`, borderRadius: 99,
            fontSize: 10, fontWeight: 500, color: colors.text,
          }}>
            <IconCompass color={colors.secondary} size={11} /> Explore
          </div>
        </div>

        {/* ── Trip Planning Cards ────────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
          {/* Classic Planner */}
          <div style={{
            backgroundColor: colors.cardBg, border: `1px solid ${colors.cardBorder}`,
            borderRadius: 12, padding: 12,
          }}>
            <div style={{
              width: 30, height: 30, borderRadius: 8,
              backgroundColor: colors.secondaryFaint,
              display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8,
            }}>
              <IconMapPin color={colors.secondary} size={14} />
            </div>
            <div style={{ fontSize: 10, fontWeight: 600, color: colors.text, marginBottom: 3 }}>Classic Planner</div>
            <div style={{ fontSize: 8, color: colors.textMuted, lineHeight: '1.3' }}>Build your itinerary step by step</div>
          </div>

          {/* Smart AI Planner */}
          <div style={{
            backgroundColor: colors.cardBg,
            border: `2px solid ${showAi ? colors.primary : colors.cardBorder}`,
            borderRadius: 12, padding: 12, position: 'relative' as const,
            opacity: showAi ? 1 : 0.5,
          }}>
            {showAi && <div style={{
              position: 'absolute' as const, top: 8, right: 8,
              width: 6, height: 6, borderRadius: 99,
              backgroundColor: colors.primary,
              boxShadow: `0 0 6px ${colors.primary}`,
            }} />}
            <div style={{
              width: 30, height: 30, borderRadius: 8,
              backgroundColor: colors.primaryFaint,
              display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8,
            }}>
              <IconSparkles color={colors.primary} size={14} />
            </div>
            <div style={{ fontSize: 10, fontWeight: 600, color: colors.text, marginBottom: 3 }}>Smart AI Planner</div>
            <div style={{ fontSize: 8, color: colors.textMuted, lineHeight: '1.3' }}>AI crafts the perfect itinerary</div>
          </div>
        </div>

        {/* ── Quick Actions Grid ─────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 8, marginBottom: 14 }}>
          {/* Cultural Affluence */}
          {showCultural && (
            <div style={{
              backgroundColor: colors.cardBg, border: `1px solid ${colors.cardBorder}`,
              borderRadius: 12, padding: '12px 14px',
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <div style={{
                width: 34, height: 34, borderRadius: 8, backgroundColor: colors.secondaryFaint,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <IconBookOpen color={colors.secondary} size={16} />
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: colors.text }}>Cultural Affluence</div>
                <div style={{ fontSize: 9, color: colors.textMuted }}>Learn etiquette, customs, and local insights</div>
              </div>
            </div>
          )}

          {/* Saved Trips */}
          <div style={{
            backgroundColor: colors.cardBg, border: `1px solid ${colors.cardBorder}`,
            borderRadius: 12, padding: '12px 14px',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div style={{
              width: 34, height: 34, borderRadius: 8, backgroundColor: colors.secondaryFaint,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <IconCompass color={colors.secondary} size={16} />
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: colors.text }}>Saved Trips</div>
              <div style={{ fontSize: 9, color: colors.textMuted }}>Access your saved itineraries and plans</div>
            </div>
          </div>

          {/* Schedule a Call */}
          {showZoom && (
            <div style={{
              backgroundColor: colors.cardBg, border: `1px solid ${colors.cardBorder}`,
              borderRadius: 12, padding: '12px 14px',
              display: 'flex', alignItems: 'center', gap: 10,
              position: 'relative' as const,
            }}>
              {/* Pro badge */}
              <div style={{
                position: 'absolute' as const, top: 7, right: 8,
                padding: '1px 5px',
                background: 'linear-gradient(to right, #f59e0b, #eab308)',
                color: '#fff', fontSize: 7, fontWeight: 700, borderRadius: 4,
                display: 'flex', alignItems: 'center', gap: 2,
              }}>
                <IconCrown color="#fff" size={7} /> Pro
              </div>
              <div style={{
                width: 34, height: 34, borderRadius: 8, backgroundColor: colors.secondaryFaint,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <IconVideo color={colors.secondary} size={16} />
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: colors.text }}>Schedule a Call</div>
                <div style={{ fontSize: 9, color: colors.textMuted }}>Plan over Zoom with your group</div>
              </div>
            </div>
          )}
        </div>

        {/* ── Upgrade Banner ─────────────────────────────────────────────── */}
        <div style={{
          borderRadius: 14, padding: '14px 16px',
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
          position: 'relative' as const, overflow: 'hidden', marginBottom: 14,
        }}>
          {/* Decorative blur */}
          <div style={{
            position: 'absolute' as const, top: -20, right: -10,
            width: 70, height: 70, borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.08)', filter: 'blur(16px)',
          }} />
          <div style={{ position: 'relative' as const, zIndex: 1 }}>
            <div style={{ fontSize: 7, fontWeight: 700, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase' as const, letterSpacing: 0.8, marginBottom: 4 }}>
              Upgrade to Navigator
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 34, height: 34, borderRadius: 10,
                backgroundColor: 'rgba(255,255,255,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <IconCrown color="#fff" size={17} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>Unlock Navigator</div>
                <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.75)', marginTop: 1 }}>Everything included — offline mode, priority support & beyond</div>
              </div>
            </div>
            <div style={{ marginTop: 10, textAlign: 'center' as const }}>
              <span style={{
                display: 'inline-block', padding: '5px 16px', backgroundColor: '#fff',
                color: colors.primary, borderRadius: 8, fontSize: 10, fontWeight: 600,
              }}>
                View Plans
              </span>
            </div>
          </div>
        </div>

      </div>

      {/* ── Bottom Navigation (pinned) ──────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-around',
        height: 52, backgroundColor: colors.cardBg,
        borderTop: `1px solid ${colors.cardBorder}`, padding: '0 4px',
      }}>
        {/* Home (active) */}
        <div style={{ display: 'flex', flexDirection: 'column' as const, alignItems: 'center', gap: 2 }}>
          <IconHome color={colors.primary} size={16} />
          <span style={{ fontSize: 8, fontWeight: 600, color: colors.primary }}>Home</span>
        </div>
        {/* Plan */}
        <div style={{ display: 'flex', flexDirection: 'column' as const, alignItems: 'center', gap: 2 }}>
          <IconMapPin color={colors.textDim} size={16} />
          <span style={{ fontSize: 8, color: colors.textDim }}>Plan</span>
        </div>
        {/* Trips */}
        <div style={{ display: 'flex', flexDirection: 'column' as const, alignItems: 'center', gap: 2 }}>
          <IconBookmark color={colors.textDim} size={16} />
          <span style={{ fontSize: 8, color: colors.textDim }}>Trips</span>
        </div>
        {/* Cultural (conditional) */}
        {showCultural && (
          <div style={{ display: 'flex', flexDirection: 'column' as const, alignItems: 'center', gap: 2 }}>
            <IconGlobe color={colors.textDim} size={16} />
            <span style={{ fontSize: 8, color: colors.textDim }}>Cultural</span>
          </div>
        )}
        {/* Avatar */}
        <div style={{ display: 'flex', flexDirection: 'column' as const, alignItems: 'center', gap: 2 }}>
          <div style={{
            width: 18, height: 18, borderRadius: 99,
            backgroundColor: isDark ? '#444' : '#d1d5db', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            fontSize: 8, fontWeight: 600, color: colors.textMuted,
          }}>S</div>
          <span style={{ fontSize: 8, color: colors.textDim }}>Profile</span>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* PREVIEW TABS — Mobile / Desktop toggle                                     */
/* ═══════════════════════════════════════════════════════════════════════════ */

function PreviewTabs({ form, features }: { form: Record<string, any>; features?: FeatureConfig }) {
  const [tab, setTab] = useState<'app-mobile' | 'app-desktop' | 'splash-mobile' | 'splash-desktop'>('splash-mobile');
  const [mode, setMode] = useState<'dark' | 'light'>('dark');

  const TABS = [
    { id: 'splash-mobile' as const, label: 'Welcome Mobile',
      icon: <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="20" x="5" y="2" rx="2" ry="2"/><path d="M12 18h.01"/></svg> },
    { id: 'splash-desktop' as const, label: 'Welcome Desktop',
      icon: <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="14" x="2" y="3" rx="2"/><path d="M8 21h8"/><path d="M12 17v4"/></svg> },
    { id: 'app-mobile' as const, label: 'App Mobile',
      icon: <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="20" x="5" y="2" rx="2" ry="2"/><path d="M12 18h.01"/></svg> },
    { id: 'app-desktop' as const, label: 'App Desktop',
      icon: <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="14" x="2" y="3" rx="2"/><path d="M8 21h8"/><path d="M12 17v4"/></svg> },
  ];

  return (
    <div>
      {/* Tab switcher (badge buttons) + mode toggle */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={cn(
              'flex items-center gap-1 py-1.5 px-2.5 rounded-lg border text-[10px] font-semibold transition-all',
              tab === t.id
                ? 'border-emerald-500/50 badge-emerald'
                : 'border-surface-700 text-surface-500 hover:border-surface-500 hover:text-surface-300'
            )}
          >
            {t.icon} {t.label}
          </button>
        ))}
        {/* Dark / Light toggle — same badge style */}
        <button
          onClick={() => setMode(m => m === 'dark' ? 'light' : 'dark')}
          className={cn(
            'flex items-center gap-1 py-1.5 px-2.5 rounded-lg border text-[10px] font-semibold transition-all ml-auto',
            mode === 'dark'
              ? 'border-emerald-500/50 badge-emerald'
              : 'border-surface-700 text-surface-500 hover:border-surface-500 hover:text-surface-300'
          )}
          title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {mode === 'dark' ? (
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/></svg>
          ) : (
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2"/><path d="M12 20v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="m17.66 17.66 1.41 1.41"/><path d="M2 12h2"/><path d="M20 12h2"/><path d="m6.34 17.66-1.41 1.41"/><path d="m19.07 4.93-1.41 1.41"/></svg>
          )}
          {mode === 'dark' ? 'Dark' : 'Light'}
        </button>
      </div>

      {/* Preview content */}
      {tab === 'app-mobile' && <LivePreview form={form} features={features} mode={mode} />}
      {tab === 'app-desktop' && <BrowserPreview form={form} features={features} mode={mode} />}
      {tab === 'splash-mobile' && <MobileSplashPreview form={form} features={features} mode={mode} />}
      {tab === 'splash-desktop' && <SplashPreview form={form} features={features} mode={mode} />}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* BROWSER PREVIEW — Desktop / Web view                                       */
/* ═══════════════════════════════════════════════════════════════════════════ */

function BrowserPreview({ form, features, mode = 'dark' }: { form: Record<string, string>; features?: FeatureConfig; mode?: 'dark' | 'light' }) {
  const isDark = mode === 'dark';
  const colors = useMemo(() => {
    const primary = form.primary_color || '#0d9488';
    const secondary = form.secondary_color || lightenColor(primary, 0.3);
    return {
      primary, secondary,
      primaryFaint: hexAlpha(primary, 0.10),
      primaryBorder: hexAlpha(primary, 0.30),
      secondaryFaint: hexAlpha(secondary, 0.10),
      bg: isDark ? '#1a1a1a' : '#faf8f6',
      cardBg: isDark ? '#292929' : '#ffffff',
      cardBorder: isDark ? '#3a3a3a' : '#e8e2db',
      text: isDark ? '#f2f2f2' : '#292929',
      textMuted: isDark ? '#999999' : '#737373',
      textDim: isDark ? '#666666' : '#999999',
      sidebarBg: isDark ? '#111111' : '#f5f2ef',
    };
  }, [form.primary_color, form.secondary_color, isDark]);

  const fontFamily = form.font_family || 'DM Sans, sans-serif';
  const appName = form.app_name || 'DiscoveryGem';
  const showCultural = features?.cultural_hub !== false;
  const showZoom = features?.zoom_meetings !== false;
  const showAi = features?.ai_trip_builder !== false;
  const domain = appName.toLowerCase().replace(/\s+/g, '') + '.discoverygem.com';

  return (
    <div style={{
      width: '100%', borderRadius: 10, overflow: 'hidden', fontFamily,
      border: `1px solid ${isDark ? '#333' : '#d1d5db'}`,
      boxShadow: isDark ? '0 12px 40px rgba(0,0,0,0.3)' : '0 12px 40px rgba(0,0,0,0.08)',
    }}>
      {/* ── Browser Chrome ─────────────────────────────────────────────── */}
      <div style={{
        height: 32, backgroundColor: isDark ? '#2a2a2a' : '#e5e7eb', display: 'flex',
        alignItems: 'center', padding: '0 10px', gap: 8,
      }}>
        {/* Traffic lights */}
        <div style={{ display: 'flex', gap: 5 }}>
          {['#ff5f57', '#febc2e', '#28c840'].map(c => (
            <div key={c} style={{ width: 9, height: 9, borderRadius: 99, backgroundColor: c }} />
          ))}
        </div>
        {/* URL bar */}
        <div style={{
          flex: 1, backgroundColor: isDark ? '#1a1a1a' : '#f8fafc', borderRadius: 5,
          padding: '3px 8px', display: 'flex', alignItems: 'center', gap: 5,
        }}>
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={isDark ? '#666' : '#94a3b8'} strokeWidth="2.5"><rect width="18" height="11" x="3" y="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          <span style={{ fontSize: 9, color: isDark ? '#888' : '#64748b', fontFamily: 'monospace' }}>{domain}</span>
        </div>
      </div>

      {/* ── Page Layout: Sidebar + Content ──────────────────────────────── */}
      <div style={{ display: 'flex', height: 480, backgroundColor: colors.bg }}>

        {/* Sidebar */}
        <div style={{
          width: 52, backgroundColor: colors.sidebarBg,
          borderRight: `1px solid ${colors.cardBorder}`,
          display: 'flex', flexDirection: 'column' as const,
          alignItems: 'center', padding: '12px 0', gap: 6,
        }}>
          {/* Logo */}
          <div style={{
            width: 30, height: 30, borderRadius: 8, marginBottom: 10,
            backgroundColor: colors.primaryFaint, border: `1px solid ${colors.primaryBorder}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 700, color: colors.primary,
          }}>
            {form.logo_url ? (
              <img src={form.logo_url} alt="" style={{ width: 20, height: 20, borderRadius: 4, objectFit: 'contain' as const }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            ) : appName.charAt(0)}
          </div>

          {/* Nav items */}
          {[
            { icon: <IconHome color={colors.primary} size={14} />, active: true },
            { icon: <IconMapPin color={colors.textDim} size={14} />, active: false },
            { icon: <IconBookmark color={colors.textDim} size={14} />, active: false },
            ...(showCultural ? [{ icon: <IconGlobe color={colors.textDim} size={14} />, active: false }] : []),
          ].map((item, i) => (
            <div key={i} style={{
              width: 34, height: 34, borderRadius: 8,
              backgroundColor: item.active ? colors.primaryFaint : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {item.icon}
            </div>
          ))}

          {/* Avatar at bottom */}
          <div style={{ marginTop: 'auto' }}>
            <div style={{
              width: 26, height: 26, borderRadius: 99, backgroundColor: isDark ? '#444' : '#d1d5db',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 600, color: colors.textMuted,
            }}>S</div>
          </div>
        </div>

        {/* Main content */}
        <div style={{
          flex: 1, overflowY: 'auto' as const, padding: '16px 20px',
          scrollbarWidth: 'none' as const,
        }}>
          {/* App badge + bell row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '3px 9px', backgroundColor: colors.primaryFaint,
              border: `1px solid ${colors.primaryBorder}`, borderRadius: 99,
              fontSize: 9, fontWeight: 600, color: colors.primary,
            }}>
              {appName}
            </div>
            <div style={{
              width: 26, height: 26, borderRadius: 99,
              backgroundColor: colors.cardBg, border: `1px solid ${colors.cardBorder}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <IconBell color={colors.secondary} size={11} />
            </div>
          </div>

          {/* Welcome */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: colors.text }}>Welcome, Sarah</span>
              <span style={{
                fontSize: 8, fontWeight: 500, color: colors.textMuted,
                backgroundColor: colors.cardBg, border: `1px solid ${colors.cardBorder}`,
                padding: '1px 6px', borderRadius: 99,
              }}>Explorer Free</span>
            </div>
            <div style={{ fontSize: 10, color: colors.textMuted }}>
              {form.welcome_message || 'Travel with cultural clarity'}
            </div>
          </div>

          {/* Quick chips */}
          <div style={{ display: 'flex', gap: 5, marginBottom: 12 }}>
            {[
              { label: 'Plan', icon: <IconMapPin color={colors.primary} size={9} /> },
              { label: 'Explore', icon: <IconCompass color={colors.secondary} size={9} /> },
            ].map(c => (
              <div key={c.label} style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                padding: '3px 8px', backgroundColor: colors.cardBg,
                border: `1px solid ${colors.cardBorder}`, borderRadius: 99,
                fontSize: 8, fontWeight: 500, color: colors.text,
              }}>
                {c.icon} {c.label}
              </div>
            ))}
          </div>

          {/* Planner cards — side by side */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 10 }}>
            <div style={{
              backgroundColor: colors.cardBg, border: `1px solid ${colors.cardBorder}`,
              borderRadius: 10, padding: 10,
            }}>
              <div style={{
                width: 24, height: 24, borderRadius: 6, backgroundColor: colors.secondaryFaint,
                display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6,
              }}>
                <IconMapPin color={colors.secondary} size={12} />
              </div>
              <div style={{ fontSize: 9, fontWeight: 600, color: colors.text }}>Classic Planner</div>
              <div style={{ fontSize: 7, color: colors.textMuted, marginTop: 2 }}>Build your itinerary step by step</div>
            </div>
            <div style={{
              backgroundColor: colors.cardBg,
              border: `2px solid ${showAi ? colors.primary : colors.cardBorder}`,
              borderRadius: 10, padding: 10, position: 'relative' as const,
              opacity: showAi ? 1 : 0.5,
            }}>
              {showAi && <div style={{
                position: 'absolute' as const, top: 6, right: 6,
                width: 5, height: 5, borderRadius: 99,
                backgroundColor: colors.primary, boxShadow: `0 0 5px ${colors.primary}`,
              }} />}
              <div style={{
                width: 24, height: 24, borderRadius: 6, backgroundColor: colors.primaryFaint,
                display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6,
              }}>
                <IconSparkles color={colors.primary} size={12} />
              </div>
              <div style={{ fontSize: 9, fontWeight: 600, color: colors.text }}>Smart AI Planner</div>
              <div style={{ fontSize: 7, color: colors.textMuted, marginTop: 2 }}>AI crafts the perfect itinerary</div>
            </div>
          </div>

          {/* Quick actions — horizontal on desktop */}
          <div style={{ display: 'grid', gridTemplateColumns: showCultural && showZoom ? '1fr 1fr 1fr' : showCultural || showZoom ? '1fr 1fr' : '1fr', gap: 6, marginBottom: 10 }}>
            {showCultural && (
              <div style={{
                backgroundColor: colors.cardBg, border: `1px solid ${colors.cardBorder}`,
                borderRadius: 10, padding: '10px 10px',
              }}>
                <div style={{
                  width: 26, height: 26, borderRadius: 6, backgroundColor: colors.secondaryFaint,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6,
                }}>
                  <IconBookOpen color={colors.secondary} size={13} />
                </div>
                <div style={{ fontSize: 8, fontWeight: 600, color: colors.text }}>Cultural Affluence</div>
                <div style={{ fontSize: 7, color: colors.textMuted, marginTop: 1 }}>Etiquette & customs</div>
              </div>
            )}
            <div style={{
              backgroundColor: colors.cardBg, border: `1px solid ${colors.cardBorder}`,
              borderRadius: 10, padding: '10px 10px',
            }}>
              <div style={{
                width: 26, height: 26, borderRadius: 6, backgroundColor: colors.secondaryFaint,
                display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6,
              }}>
                <IconCompass color={colors.secondary} size={13} />
              </div>
              <div style={{ fontSize: 8, fontWeight: 600, color: colors.text }}>Saved Trips</div>
              <div style={{ fontSize: 7, color: colors.textMuted, marginTop: 1 }}>Your itineraries</div>
            </div>
            {showZoom && (
              <div style={{
                backgroundColor: colors.cardBg, border: `1px solid ${colors.cardBorder}`,
                borderRadius: 10, padding: '10px 10px', position: 'relative' as const,
              }}>
                <div style={{
                  position: 'absolute' as const, top: 5, right: 6,
                  padding: '1px 4px', background: 'linear-gradient(to right, #f59e0b, #eab308)',
                  color: '#fff', fontSize: 6, fontWeight: 700, borderRadius: 3,
                  display: 'flex', alignItems: 'center', gap: 1,
                }}>
                  <IconCrown color="#fff" size={6} /> Pro
                </div>
                <div style={{
                  width: 26, height: 26, borderRadius: 6, backgroundColor: colors.secondaryFaint,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6,
                }}>
                  <IconVideo color={colors.secondary} size={13} />
                </div>
                <div style={{ fontSize: 8, fontWeight: 600, color: colors.text }}>Schedule a Call</div>
                <div style={{ fontSize: 7, color: colors.textMuted, marginTop: 1 }}>Zoom with group</div>
              </div>
            )}
          </div>

          {/* Upgrade banner */}
          <div style={{
            borderRadius: 10, padding: '10px 12px',
            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
            position: 'relative' as const, overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute' as const, top: -15, right: -8,
              width: 50, height: 50, borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.08)', filter: 'blur(12px)',
            }} />
            <div style={{ position: 'relative' as const, zIndex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 28, height: 28, borderRadius: 8,
                backgroundColor: 'rgba(255,255,255,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <IconCrown color="#fff" size={14} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 6, fontWeight: 700, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase' as const, letterSpacing: 0.5 }}>Upgrade to Navigator</div>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#fff' }}>Unlock Navigator</div>
              </div>
              <span style={{
                padding: '3px 10px', backgroundColor: '#fff',
                color: colors.primary, borderRadius: 6, fontSize: 8, fontWeight: 600,
              }}>View Plans</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* MOBILE SPLASH PREVIEW — Splash page in phone frame                          */
/* ═══════════════════════════════════════════════════════════════════════════ */

function MobileSplashPreview({ form, features, mode = 'light' }: { form: Record<string, any>; features?: FeatureConfig; mode?: 'dark' | 'light' }) {
  const isDark = mode === 'dark';
  const colors = useMemo(() => {
    const primary = form.primary_color || '#0d9488';
    const secondary = form.secondary_color || lightenColor(primary, 0.3);
    return { primary, secondary };
  }, [form.primary_color, form.secondary_color]);

  const fontFamily = form.font_family || 'DM Sans, sans-serif';
  const appName = form.app_name || 'DiscoveryGem';
  const headline = form.hero_headline || 'Plan your next adventure';
  const subtitle = form.hero_subtitle || 'Discover culture, not just destinations';
  const ctaLabel = form.cta_label || 'Start Traveling';
  const companyName = form.company_name || 'Arrive Tech LLC';
  const featuresHeading = form.feature_cards_heading || 'Everything you need to travel smarter';
  const infoHeading = form.info_cards_heading || 'Cultural Insights';
  const infoSubtitle = form.info_cards_subtitle || 'Travel respectfully with essential tips';

  // Feature cards
  const selectedFeatureCards = useMemo(() => {
    const selectedSlugs = (form.feature_cards || []) as string[];
    if (selectedSlugs.length > 0) {
      return FEATURE_CARD_OPTIONS.filter(c => selectedSlugs.includes(c.slug)).slice(0, 6);
    }
    const enabled = FEATURE_CARD_OPTIONS.filter(c => {
      if (!features) return true;
      return (features as any)[c.slug] === true;
    });
    return enabled.slice(0, 3);
  }, [form.feature_cards, features]);

  // Info cards
  const infoCards = useMemo(() => {
    if (form.info_cards_mode === 'custom') {
      return ((form.info_cards_custom || []) as { title: string; description: string }[]).filter((c: any) => c.title);
    }
    const selectedPresets = (form.info_cards_presets || []) as string[];
    if (selectedPresets.length > 0) {
      return INFO_CARD_PRESETS.filter(p => selectedPresets.includes(p.slug)).map(p => ({ title: p.title, description: '' }));
    }
    return INFO_CARD_PRESETS.slice(0, 4).map(p => ({ title: p.title, description: '' }));
  }, [form.info_cards_mode, form.info_cards_custom, form.info_cards_presets]);

  return (
    <div style={{
      width: '100%', maxWidth: 320, margin: '0 auto',
      borderRadius: 40, border: '8px solid #d1d5db',
      backgroundColor: '#e5e7eb',
      boxShadow: '0 25px 60px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.06)',
      overflow: 'hidden', fontFamily,
      display: 'flex', flexDirection: 'column' as const,
    }}>
      {/* Status bar */}
      <div style={{
        height: 44, backgroundColor: isDark ? '#0f172a' : '#fff', display: 'flex',
        alignItems: 'center', justifyContent: 'space-between',
        padding: '0 24px', position: 'relative' as const,
      }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b' }}>9:41</span>
        <div style={{
          position: 'absolute' as const, top: 0, left: '50%', transform: 'translateX(-50%)',
          width: 100, height: 26, backgroundColor: '#000', borderRadius: '0 0 16px 16px',
        }} />
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 1.5 }}>
            {[10, 12, 14, 16].map((h) => (
              <div key={h} style={{ width: 3, height: h, borderRadius: 1, backgroundColor: '#1e293b', opacity: 0.6 }} />
            ))}
          </div>
          <div style={{ width: 20, height: 9, borderRadius: 2, border: '1px solid rgba(30,41,59,0.4)', position: 'relative' as const, marginLeft: 2 }}>
            <div style={{ position: 'absolute' as const, top: 1, left: 1, bottom: 1, width: '70%', borderRadius: 1, backgroundColor: '#1e293b', opacity: 0.6 }} />
          </div>
        </div>
      </div>

      {/* Scrollable splash content */}
      <div style={{
        height: 550, overflowY: 'auto' as const, backgroundColor: isDark ? '#0f172a' : '#fff',
        scrollbarWidth: 'none' as const,
      }}>
        {/* Header */}
        <div style={{
          height: 36, borderBottom: `1px solid ${isDark ? '#1e293b' : '#f1f5f9'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 12px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            {form.logo_url ? (
              <img src={form.logo_url} alt="" style={{ width: 16, height: 16, objectFit: 'contain' as const, filter: isDark ? 'brightness(1.2)' : undefined }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            ) : (
              <div style={{ width: 16, height: 16, borderRadius: 3, backgroundColor: colors.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 7, fontWeight: 700, color: '#fff' }}>
                {appName.charAt(0)}
              </div>
            )}
            <span style={{ fontSize: 9, fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>{appName}</span>
          </div>
          <div style={{
            padding: '2px 8px', borderRadius: 99,
            background: `linear-gradient(to right, ${colors.primary}, ${colors.secondary})`,
            fontSize: 7, fontWeight: 600, color: '#fff',
          }}>Sign In</div>
        </div>

        {/* Hero */}
        <div style={{ padding: '18px 14px 12px', textAlign: 'center' as const }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: isDark ? '#f1f5f9' : '#1e293b', lineHeight: 1.3, marginBottom: 4 }}>
            {headline}
          </div>
          <div style={{ fontSize: 7, color: isDark ? '#94a3b8' : '#64748b', lineHeight: 1.4, marginBottom: 10 }}>
            {subtitle}
          </div>
          <div style={{
            display: 'inline-block', padding: '4px 12px', borderRadius: 99,
            background: `linear-gradient(to right, ${colors.primary}, ${colors.secondary})`,
            fontSize: 8, fontWeight: 600, color: '#fff',
          }}>
            {ctaLabel} &rsaquo;
          </div>
        </div>

        {/* Destinations (conditional) */}
        {form.show_destinations && (() => {
          const isCustom = !!form.use_custom_destinations;
          const layout = isCustom ? (form.custom_destinations_layout || 'cards') : (form.destinations_layout || 'grid');
          const d = PREVIEW_SAMPLE_DESTS;
          return (
            <div style={{ padding: '6px 10px 8px' }}>
              <div style={{ fontSize: 8, fontWeight: 700, color: isDark ? '#f1f5f9' : '#1e293b', textAlign: 'center' as const, marginBottom: 5 }}>
                {isCustom ? 'Featured Destinations' : 'Popular Destinations'}
              </div>

              {/* ── Default: Grid ── */}
              {!isCustom && layout === 'grid' && (
                <div style={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' as const }}>
                  {d.map(x => (
                    <div key={x.name} style={{ width: 40, height: 28, borderRadius: 5, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: 2, fontSize: 5, fontWeight: 600, color: '#fff', background: 'linear-gradient(180deg, #94a3b8, #64748b)' }}>{x.name}</div>
                  ))}
                </div>
              )}
              {/* ── Default: Carousel ── */}
              {!isCustom && layout === 'carousel' && (
                <div style={{ display: 'flex', gap: 3, overflowX: 'auto' as const }}>
                  {[...d, { name: 'Rome', sub: 'Italy', price: '$500+', season: 'Apr-Oct' }, { name: 'NYC', sub: 'USA', price: '$900+', season: 'Year-round' }].map(x => (
                    <div key={x.name} style={{ flexShrink: 0, width: 36, height: 26, borderRadius: 5, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: 2, fontSize: 4, fontWeight: 600, color: '#fff', background: 'linear-gradient(180deg, #94a3b8, #64748b)' }}>{x.name}</div>
                  ))}
                </div>
              )}
              {/* ── Default: List ── */}
              {!isCustom && layout === 'list' && (
                <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 2 }}>
                  {d.slice(0, 3).map(x => (
                    <div key={x.name} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 5px', borderRadius: 4, border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, backgroundColor: isDark ? '#1e293b' : '#fff' }}>
                      <div style={{ width: 20, height: 14, borderRadius: 3, background: 'linear-gradient(180deg, #94a3b8, #64748b)', flexShrink: 0 }} />
                      <span style={{ fontSize: 5, fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b' }}>{x.name}, {x.sub}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Custom: Cards — 2-col card grid with image, title, subtitle, price ── */}
              {isCustom && layout === 'cards' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                  {d.map(x => (
                    <div key={x.name} style={{ borderRadius: 5, overflow: 'hidden', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, backgroundColor: isDark ? '#1e293b' : '#fff' }}>
                      <div style={{ height: 20, background: 'linear-gradient(180deg, #94a3b8, #64748b)' }} />
                      <div style={{ padding: '2px 3px' }}>
                        <div style={{ fontSize: 5, fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>{x.name}</div>
                        <div style={{ fontSize: 3.5, color: isDark ? '#94a3b8' : '#64748b' }}>{x.sub} · {x.price}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Custom: Magazine — hero card + smaller grid ── */}
              {isCustom && layout === 'magazine' && (
                <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 3 }}>
                  <div style={{ borderRadius: 5, overflow: 'hidden', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}` }}>
                    <div style={{ height: 36, background: 'linear-gradient(180deg, #7c8fa0, #4a5568)', display: 'flex', alignItems: 'flex-end', padding: 3 }}>
                      <div>
                        <div style={{ fontSize: 7, fontWeight: 700, color: '#fff' }}>{d[0].name}</div>
                        <div style={{ fontSize: 4, color: '#e2e8f0' }}>{d[0].sub} · {d[0].price}</div>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                    {d.slice(1).map(x => (
                      <div key={x.name} style={{ borderRadius: 4, overflow: 'hidden', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}` }}>
                        <div style={{ height: 16, background: 'linear-gradient(180deg, #94a3b8, #64748b)' }} />
                        <div style={{ padding: '1px 2px', backgroundColor: isDark ? '#1e293b' : '#fff' }}>
                          <div style={{ fontSize: 4, fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b' }}>{x.name}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Custom: Carousel — horizontal scroll with detail cards ── */}
              {isCustom && layout === 'carousel' && (
                <div style={{ display: 'flex', gap: 3, overflowX: 'auto' as const }}>
                  {d.map(x => (
                    <div key={x.name} style={{ flexShrink: 0, width: 50, borderRadius: 5, overflow: 'hidden', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, backgroundColor: isDark ? '#1e293b' : '#fff' }}>
                      <div style={{ height: 24, background: 'linear-gradient(180deg, #94a3b8, #64748b)' }} />
                      <div style={{ padding: '2px 3px' }}>
                        <div style={{ fontSize: 5, fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>{x.name}</div>
                        <div style={{ fontSize: 3, color: isDark ? '#94a3b8' : '#64748b' }}>{x.sub}</div>
                        <div style={{ fontSize: 3, color: colors.primary, fontWeight: 600 }}>{x.price}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Custom: Masonry — staggered heights ── */}
              {isCustom && layout === 'masonry' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                  {d.map((x, i) => (
                    <div key={x.name} style={{ borderRadius: 5, overflow: 'hidden', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, backgroundColor: isDark ? '#1e293b' : '#fff' }}>
                      <div style={{ height: i % 2 === 0 ? 28 : 18, background: 'linear-gradient(180deg, #94a3b8, #64748b)' }} />
                      <div style={{ padding: '2px 3px' }}>
                        <div style={{ fontSize: 5, fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>{x.name}</div>
                        <div style={{ fontSize: 3, color: isDark ? '#94a3b8' : '#64748b' }}>{x.sub}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Custom: Overlay — full-bleed image with gradient text ── */}
              {isCustom && layout === 'overlay' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                  {d.map(x => (
                    <div key={x.name} style={{ height: 32, borderRadius: 5, overflow: 'hidden', position: 'relative' as const, background: 'linear-gradient(180deg, #7c8fa0, #3a4a5a)' }}>
                      <div style={{ position: 'absolute' as const, bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(0,0,0,0.7))', padding: '6px 3px 2px' }}>
                        <div style={{ fontSize: 5, fontWeight: 700, color: '#fff' }}>{x.name}</div>
                        <div style={{ fontSize: 3, color: '#e2e8f0' }}>{x.sub}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Custom: Minimal — clean compact list ── */}
              {isCustom && layout === 'minimal' && (
                <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 2 }}>
                  {d.map(x => (
                    <div key={x.name} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 4px', borderRadius: 4, borderBottom: `1px solid ${isDark ? '#1e293b' : '#f1f5f9'}` }}>
                      <div style={{ width: 16, height: 12, borderRadius: 2, background: 'linear-gradient(180deg, #94a3b8, #64748b)', flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 5, fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b' }}>{x.name}</div>
                        <div style={{ fontSize: 3, color: isDark ? '#94a3b8' : '#64748b' }}>{x.sub}</div>
                      </div>
                      <div style={{ fontSize: 4, color: colors.primary, fontWeight: 600 }}>{x.price}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* Feature Cards */}
        <div style={{ padding: '6px 10px 8px' }}>
          <div style={{ fontSize: 8, fontWeight: 700, color: isDark ? '#f1f5f9' : '#1e293b', textAlign: 'center' as const, marginBottom: 5 }}>
            {featuresHeading}
          </div>

          {/* Grid layout */}
          {(!form.feature_cards_layout || form.feature_cards_layout === 'grid') && (
            <div style={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' as const }}>
              {selectedFeatureCards.slice(0, 3).map(card => {
                const CardIcon = card.icon;
                return (
                  <div key={card.slug} style={{
                    flex: '1 1 28%', maxWidth: 70, textAlign: 'center' as const,
                    padding: 5, borderRadius: 6, backgroundColor: isDark ? '#1e293b' : '#f8fafc',
                  }}>
                    <div style={{
                      width: 18, height: 18, borderRadius: 5, margin: '0 auto 3px',
                      background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <CardIcon size={9} color="#fff" strokeWidth={2.5} />
                    </div>
                    <div style={{ fontSize: 6, fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b' }}>{card.title}</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Horizontal layout */}
          {form.feature_cards_layout === 'horizontal' && (
            <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 2 }}>
              {selectedFeatureCards.slice(0, 3).map(card => {
                const CardIcon = card.icon;
                return (
                  <div key={card.slug} style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    padding: '4px 6px', borderRadius: 5,
                    border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, backgroundColor: isDark ? '#1e293b' : '#fff',
                  }}>
                    <div style={{
                      width: 16, height: 16, borderRadius: 3, flexShrink: 0,
                      background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <CardIcon size={8} color="#fff" strokeWidth={2.5} />
                    </div>
                    <div style={{ fontSize: 6, fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b' }}>{card.title}</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Carousel layout */}
          {form.feature_cards_layout === 'carousel' && (
            <div style={{ display: 'flex', gap: 3, overflowX: 'auto' as const }}>
              {selectedFeatureCards.slice(0, 4).map(card => {
                const CardIcon = card.icon;
                return (
                  <div key={card.slug} style={{
                    flexShrink: 0, width: 50, textAlign: 'center' as const,
                    padding: '5px 3px', borderRadius: 6,
                    border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, backgroundColor: isDark ? '#1e293b' : '#fff',
                  }}>
                    <div style={{
                      width: 16, height: 16, borderRadius: 4, margin: '0 auto 2px',
                      background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <CardIcon size={8} color="#fff" strokeWidth={2.5} />
                    </div>
                    <div style={{ fontSize: 5, fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b' }}>{card.title}</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Badges layout */}
          {form.feature_cards_layout === 'badges' && (
            <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap' as const, justifyContent: 'center' }}>
              {selectedFeatureCards.slice(0, 4).map(card => {
                const CardIcon = card.icon;
                return (
                  <div key={card.slug} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 2,
                    padding: '2px 6px 2px 3px', borderRadius: 99,
                    border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, backgroundColor: isDark ? '#1e293b' : '#fff',
                  }}>
                    <div style={{
                      width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                      background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <CardIcon size={7} color="#fff" strokeWidth={2.5} />
                    </div>
                    <span style={{ fontSize: 5, fontWeight: 600, color: isDark ? '#cbd5e1' : '#334155' }}>{card.title}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Info Cards */}
        {infoCards.length > 0 && (
          <div style={{ padding: '6px 10px 8px', backgroundColor: isDark ? '#1e293b' : '#f0fdfa' }}>
            <div style={{ fontSize: 8, fontWeight: 700, color: isDark ? '#f1f5f9' : '#1e293b', textAlign: 'center' as const, marginBottom: 1 }}>
              {infoHeading}
            </div>
            <div style={{ fontSize: 6, color: isDark ? '#94a3b8' : '#64748b', textAlign: 'center' as const, marginBottom: 4 }}>
              {infoSubtitle}
            </div>

            {/* Grid layout */}
            {(!form.info_cards_layout || form.info_cards_layout === 'grid') && (
              <div style={{
                display: infoCards.length === 1 ? 'flex' : 'grid',
                ...(infoCards.length === 1
                  ? { justifyContent: 'center' }
                  : { gridTemplateColumns: '1fr 1fr' }),
                gap: 3,
              }}>
                {infoCards.slice(0, 4).map((card, i) => (
                  <div key={i} style={{
                    padding: '4px 6px', borderRadius: 5,
                    backgroundColor: isDark ? '#0f172a' : '#fff', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                    ...(infoCards.length === 1 ? { maxWidth: 100 } : {}),
                  }}>
                    <div style={{ fontSize: 6, fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b' }}>{card.title}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Horizontal layout */}
            {form.info_cards_layout === 'horizontal' && (
              <div style={{
                display: 'flex', flexDirection: 'column' as const, gap: 2,
                ...(infoCards.length <= 2 ? { maxWidth: 140, margin: '0 auto' } : {}),
              }}>
                {infoCards.slice(0, 4).map((card, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    padding: '3px 5px', borderRadius: 4,
                    backgroundColor: isDark ? '#0f172a' : '#fff', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                  }}>
                    <div style={{
                      width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                      background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                    }} />
                    <div style={{ fontSize: 6, fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b' }}>{card.title}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Carousel layout */}
            {form.info_cards_layout === 'carousel' && (
              <div style={{
                display: 'flex', gap: 3, overflowX: 'auto' as const,
                justifyContent: infoCards.length <= 2 ? 'center' : undefined,
              }}>
                {infoCards.slice(0, 6).map((card, i) => (
                  <div key={i} style={{
                    flexShrink: 0, width: 50, textAlign: 'center' as const,
                    padding: '4px 3px', borderRadius: 5,
                    backgroundColor: isDark ? '#0f172a' : '#fff', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                  }}>
                    <div style={{ fontSize: 5, fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b' }}>{card.title}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Content Hub — Tabbed Feeds */}
        {(form.show_youtube_feed || form.show_blog_feed || form.show_instagram_feed) && (
          <div style={{ padding: '8px 10px', backgroundColor: isDark ? '#0f172a' : '#f8fafc' }}>
            {/* Pill Tab Bar */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 6 }}>
              <div style={{
                display: 'inline-flex', gap: 2, padding: 2, borderRadius: 99,
                backgroundColor: isDark ? '#1e293b' : 'rgba(255,255,255,0.8)',
                border: `1px solid ${isDark ? '#334155' : 'rgba(226,232,240,0.6)'}`,
              }}>
                {(form.show_youtube_feed || form.show_blog_feed) && (
                  <div style={{
                    padding: '2px 6px', borderRadius: 99, fontSize: 4, fontWeight: 600,
                    background: `linear-gradient(to right, ${colors.primary}, ${colors.secondary})`,
                    color: '#fff',
                  }}>✦ Latest</div>
                )}
                {form.show_youtube_feed && (
                  <div style={{ padding: '2px 6px', borderRadius: 99, fontSize: 4, fontWeight: 500, color: isDark ? '#94a3b8' : '#64748b' }}>▶ Videos</div>
                )}
                {form.show_blog_feed && (
                  <div style={{ padding: '2px 6px', borderRadius: 99, fontSize: 4, fontWeight: 500, color: isDark ? '#94a3b8' : '#64748b' }}>📝 Blog</div>
                )}
                {form.show_instagram_feed && (
                  <div style={{ padding: '2px 6px', borderRadius: 99, fontSize: 4, fontWeight: 500, color: isDark ? '#94a3b8' : '#64748b' }}>📷 Insta</div>
                )}
              </div>
            </div>

            {/* Mixed Feed Preview (Latest tab active) */}
            <div style={{ display: 'flex', gap: 4 }}>
              {[1,2,3].map(i => (
                <div key={i} style={{ flex: 1, borderRadius: 4, overflow: 'hidden', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, backgroundColor: isDark ? '#0f172a' : '#fff' }}>
                  <div style={{ aspectRatio: '16/9', backgroundColor: isDark ? '#1e293b' : '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' as const }}>
                    {i <= 2 && (
                      <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ width: 0, height: 0, borderLeft: '3px solid white', borderTop: '2px solid transparent', borderBottom: '2px solid transparent', marginLeft: 0.5 }} />
                      </div>
                    )}
                  </div>
                  <div style={{ padding: 3 }}>
                    <div style={{ display: 'flex', gap: 2, marginBottom: 1 }}>
                      <span style={{
                        fontSize: 3, fontWeight: 700, padding: '0.5px 2px', borderRadius: 99, textTransform: 'uppercase' as const,
                        backgroundColor: i <= 2 ? (isDark ? 'rgba(127,29,29,0.3)' : '#fee2e2') : (isDark ? 'rgba(30,58,138,0.3)' : '#dbeafe'),
                        color: i <= 2 ? (isDark ? '#fca5a5' : '#b91c1c') : (isDark ? '#93c5fd' : '#1d4ed8'),
                      }}>{i <= 2 ? 'VIDEO' : 'ARTICLE'}</span>
                    </div>
                    <div style={{ fontSize: 4, fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b' }}>Content {i}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ padding: '8px 10px', backgroundColor: '#0f172a' }}>
          <div style={{ textAlign: 'center' as const }}>
            <span style={{ fontSize: 5, color: '#64748b' }}>
              &copy; {new Date().getFullYear()} {companyName}. All rights reserved.
            </span>
          </div>
        </div>
      </div>

      {/* Bottom home indicator */}
      <div style={{
        height: 20, backgroundColor: isDark ? '#0f172a' : '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{ width: 60, height: 4, borderRadius: 2, backgroundColor: isDark ? '#334155' : '#d1d5db' }} />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* SPLASH PREVIEW — Desktop landing page mockup                                */
/* ═══════════════════════════════════════════════════════════════════════════ */

function SplashPreview({ form, features, mode = 'light' }: { form: Record<string, any>; features?: FeatureConfig; mode?: 'dark' | 'light' }) {
  const isDark = mode === 'dark';
  const colors = useMemo(() => {
    const primary = form.primary_color || '#0d9488';
    const secondary = form.secondary_color || lightenColor(primary, 0.3);
    return { primary, secondary };
  }, [form.primary_color, form.secondary_color]);

  const appName = form.app_name || 'DiscoveryGem';
  const headline = form.hero_headline || 'Plan your next adventure';
  const subtitle = form.hero_subtitle || 'Discover culture, not just destinations';
  const ctaLabel = form.cta_label || 'Start Traveling';
  const companyName = form.company_name || 'Arrive Tech LLC';

  // Feature cards: use selected or first 3 enabled
  const selectedFeatureCards = useMemo(() => {
    const selectedSlugs = (form.feature_cards || []) as string[];
    if (selectedSlugs.length > 0) {
      return FEATURE_CARD_OPTIONS.filter(c => selectedSlugs.includes(c.slug)).slice(0, 6);
    }
    // Auto-select first 3 enabled
    const enabled = FEATURE_CARD_OPTIONS.filter(c => {
      if (!features) return true;
      return (features as any)[c.slug] === true;
    });
    return enabled.slice(0, 3);
  }, [form.feature_cards, features]);

  // Info cards
  const infoCards = useMemo(() => {
    if (form.info_cards_mode === 'custom') {
      return ((form.info_cards_custom || []) as { title: string; description: string }[])
        .filter((c: any) => c.title);
    }
    const selectedPresets = (form.info_cards_presets || []) as string[];
    if (selectedPresets.length > 0) {
      return INFO_CARD_PRESETS.filter(p => selectedPresets.includes(p.slug))
        .map(p => ({ title: p.title, description: '' }));
    }
    return INFO_CARD_PRESETS.slice(0, 6).map(p => ({ title: p.title, description: '' }));
  }, [form.info_cards_mode, form.info_cards_custom, form.info_cards_presets]);

  // Social links
  const socialLinks = useMemo(() => {
    const links = [
      { key: 'facebook_url', label: 'Fb' },
      { key: 'instagram_url', label: 'Ig' },
      { key: 'x_url', label: 'X' },
      { key: 'youtube_url', label: 'Yt' },
      { key: 'linkedin_url', label: 'Li' },
    ];
    const configured = links.filter(l => form[l.key]);
    return configured.length > 0 ? configured : links;
  }, [form.facebook_url, form.instagram_url, form.x_url, form.youtube_url, form.linkedin_url]);

  const featuresHeading = form.feature_cards_heading || 'Everything you need to travel smarter';
  const infoHeading = form.info_cards_heading || 'Cultural Insights';
  const infoSubtitle = form.info_cards_subtitle || 'Travel respectfully with essential tips';

  return (
    <div style={{
      width: '100%', borderRadius: 10, overflow: 'hidden',
      border: `1px solid ${isDark ? '#334155' : '#d1d5db'}`,
      boxShadow: '0 12px 40px rgba(0,0,0,0.08)',
      fontFamily: form.font_family || 'DM Sans, sans-serif',
      backgroundColor: isDark ? '#0f172a' : '#ffffff',
    }}>
      {/* ── Browser Chrome ─────────────────────────────────────────────── */}
      <div style={{
        height: 28, backgroundColor: isDark ? '#1e293b' : '#e5e7eb', display: 'flex',
        alignItems: 'center', padding: '0 10px', gap: 8,
      }}>
        <div style={{ display: 'flex', gap: 5 }}>
          {['#ff5f57', '#febc2e', '#28c840'].map(c => (
            <div key={c} style={{ width: 8, height: 8, borderRadius: 99, backgroundColor: c }} />
          ))}
        </div>
        <div style={{
          flex: 1, backgroundColor: '#f8fafc', borderRadius: 5,
          padding: '2px 8px', display: 'flex', alignItems: 'center', gap: 4,
        }}>
          <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5"><rect width="18" height="11" x="3" y="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          <span style={{ fontSize: 8, color: '#64748b', fontFamily: 'monospace' }}>{appName.toLowerCase().replace(/\s+/g, '')}.discoverygem.com</span>
        </div>
      </div>

      {/* ── Splash Content ─────────────────────────────────────────────── */}
      <div style={{ height: 500, overflowY: 'auto', scrollbarWidth: 'none' as const }}>
        {/* Header */}
        <div style={{
          height: 36, borderBottom: `1px solid ${isDark ? '#1e293b' : '#f1f5f9'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 12px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            {form.logo_url ? (
              <img src={form.logo_url} alt="" style={{ width: 18, height: 18, objectFit: 'contain' as const, filter: isDark ? 'brightness(1.2)' : undefined }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            ) : (
              <div style={{ width: 18, height: 18, borderRadius: 4, backgroundColor: colors.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 700, color: '#fff' }}>
                {appName.charAt(0)}
              </div>
            )}
            <span style={{ fontSize: 10, fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>{appName}</span>
          </div>
          <div style={{
            padding: '3px 10px', borderRadius: 99,
            background: `linear-gradient(to right, ${colors.primary}, ${colors.secondary})`,
            fontSize: 8, fontWeight: 600, color: '#fff',
          }}>Sign In</div>
        </div>

        {/* Hero */}
        <div style={{ padding: '20px 16px 14px', textAlign: 'center' as const }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: isDark ? '#f1f5f9' : '#1e293b', lineHeight: 1.3, marginBottom: 6 }}>
            {headline}
          </div>
          <div style={{ fontSize: 8, color: isDark ? '#94a3b8' : '#64748b', lineHeight: 1.4, marginBottom: 12 }}>
            {subtitle}
          </div>
          <div style={{
            display: 'inline-block', padding: '5px 14px', borderRadius: 99,
            background: `linear-gradient(to right, ${colors.primary}, ${colors.secondary})`,
            fontSize: 9, fontWeight: 600, color: '#fff',
          }}>
            {ctaLabel} &rsaquo;
          </div>
        </div>

        {/* Destinations (conditional) */}
        {form.show_destinations && (() => {
          const isCustom = !!form.use_custom_destinations;
          const layout = isCustom ? (form.custom_destinations_layout || 'cards') : (form.destinations_layout || 'grid');
          const d = PREVIEW_SAMPLE_DESTS;
          return (
            <div style={{ padding: '8px 12px 10px' }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: isDark ? '#f1f5f9' : '#1e293b', textAlign: 'center' as const, marginBottom: 6 }}>
                {isCustom ? 'Featured Destinations' : 'Popular Destinations'}
              </div>

              {/* ── Default: Grid ── */}
              {!isCustom && layout === 'grid' && (
                <div style={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap' as const }}>
                  {d.map(x => (
                    <div key={x.name} style={{ width: 44, height: 32, borderRadius: 6, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: 3, fontSize: 6, fontWeight: 600, color: '#fff', background: 'linear-gradient(180deg, #94a3b8, #64748b)' }}>{x.name}</div>
                  ))}
                </div>
              )}
              {/* ── Default: Carousel ── */}
              {!isCustom && layout === 'carousel' && (
                <div style={{ display: 'flex', gap: 4, overflowX: 'auto' as const }}>
                  {[...d, { name: 'Rome', sub: 'Italy', price: '$500+', season: 'Apr-Oct' }, { name: 'NYC', sub: 'USA', price: '$900+', season: 'Year-round' }].map(x => (
                    <div key={x.name} style={{ flexShrink: 0, width: 42, height: 30, borderRadius: 6, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: 3, fontSize: 5, fontWeight: 600, color: '#fff', background: 'linear-gradient(180deg, #94a3b8, #64748b)' }}>{x.name}</div>
                  ))}
                </div>
              )}
              {/* ── Default: List ── */}
              {!isCustom && layout === 'list' && (
                <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 3 }}>
                  {d.slice(0, 3).map(x => (
                    <div key={x.name} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 6px', borderRadius: 5, border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, backgroundColor: isDark ? '#1e293b' : '#fff' }}>
                      <div style={{ width: 24, height: 16, borderRadius: 4, background: 'linear-gradient(180deg, #94a3b8, #64748b)', flexShrink: 0 }} />
                      <span style={{ fontSize: 6, fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b' }}>{x.name}, {x.sub}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Custom: Cards — 2-col card grid ── */}
              {isCustom && layout === 'cards' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                  {d.map(x => (
                    <div key={x.name} style={{ borderRadius: 6, overflow: 'hidden', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, backgroundColor: isDark ? '#1e293b' : '#fff' }}>
                      <div style={{ height: 24, background: 'linear-gradient(180deg, #94a3b8, #64748b)' }} />
                      <div style={{ padding: '3px 4px' }}>
                        <div style={{ fontSize: 6, fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>{x.name}</div>
                        <div style={{ fontSize: 4, color: isDark ? '#94a3b8' : '#64748b' }}>{x.sub} · {x.price}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Custom: Magazine — hero + smaller grid ── */}
              {isCustom && layout === 'magazine' && (
                <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 4 }}>
                  <div style={{ borderRadius: 6, overflow: 'hidden', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}` }}>
                    <div style={{ height: 44, background: 'linear-gradient(180deg, #7c8fa0, #4a5568)', display: 'flex', alignItems: 'flex-end', padding: 4 }}>
                      <div>
                        <div style={{ fontSize: 8, fontWeight: 700, color: '#fff' }}>{d[0].name}</div>
                        <div style={{ fontSize: 5, color: '#e2e8f0' }}>{d[0].sub} · {d[0].price}</div>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 3 }}>
                    {d.slice(1).map(x => (
                      <div key={x.name} style={{ borderRadius: 5, overflow: 'hidden', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}` }}>
                        <div style={{ height: 18, background: 'linear-gradient(180deg, #94a3b8, #64748b)' }} />
                        <div style={{ padding: '2px 3px', backgroundColor: isDark ? '#1e293b' : '#fff' }}>
                          <div style={{ fontSize: 5, fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b' }}>{x.name}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Custom: Carousel — horizontal scroll with detail cards ── */}
              {isCustom && layout === 'carousel' && (
                <div style={{ display: 'flex', gap: 4, overflowX: 'auto' as const }}>
                  {d.map(x => (
                    <div key={x.name} style={{ flexShrink: 0, width: 56, borderRadius: 6, overflow: 'hidden', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, backgroundColor: isDark ? '#1e293b' : '#fff' }}>
                      <div style={{ height: 28, background: 'linear-gradient(180deg, #94a3b8, #64748b)' }} />
                      <div style={{ padding: '3px 4px' }}>
                        <div style={{ fontSize: 5, fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>{x.name}</div>
                        <div style={{ fontSize: 3.5, color: isDark ? '#94a3b8' : '#64748b' }}>{x.sub}</div>
                        <div style={{ fontSize: 3.5, color: colors.primary, fontWeight: 600 }}>{x.price}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Custom: Masonry — staggered heights ── */}
              {isCustom && layout === 'masonry' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                  {d.map((x, i) => (
                    <div key={x.name} style={{ borderRadius: 6, overflow: 'hidden', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, backgroundColor: isDark ? '#1e293b' : '#fff' }}>
                      <div style={{ height: i % 2 === 0 ? 34 : 20, background: 'linear-gradient(180deg, #94a3b8, #64748b)' }} />
                      <div style={{ padding: '3px 4px' }}>
                        <div style={{ fontSize: 5, fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>{x.name}</div>
                        <div style={{ fontSize: 3.5, color: isDark ? '#94a3b8' : '#64748b' }}>{x.sub}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Custom: Overlay — full-bleed image with gradient text ── */}
              {isCustom && layout === 'overlay' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                  {d.map(x => (
                    <div key={x.name} style={{ height: 38, borderRadius: 6, overflow: 'hidden', position: 'relative' as const, background: 'linear-gradient(180deg, #7c8fa0, #3a4a5a)' }}>
                      <div style={{ position: 'absolute' as const, bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(0,0,0,0.7))', padding: '8px 4px 3px' }}>
                        <div style={{ fontSize: 6, fontWeight: 700, color: '#fff' }}>{x.name}</div>
                        <div style={{ fontSize: 3.5, color: '#e2e8f0' }}>{x.sub}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Custom: Minimal — clean compact list ── */}
              {isCustom && layout === 'minimal' && (
                <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 3 }}>
                  {d.map(x => (
                    <div key={x.name} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 5px', borderRadius: 4, borderBottom: `1px solid ${isDark ? '#1e293b' : '#f1f5f9'}` }}>
                      <div style={{ width: 20, height: 14, borderRadius: 3, background: 'linear-gradient(180deg, #94a3b8, #64748b)', flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 6, fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b' }}>{x.name}</div>
                        <div style={{ fontSize: 3.5, color: isDark ? '#94a3b8' : '#64748b' }}>{x.sub}</div>
                      </div>
                      <div style={{ fontSize: 5, color: colors.primary, fontWeight: 600 }}>{x.price}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* Feature Cards */}
        <div style={{ padding: '8px 12px 10px' }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: isDark ? '#f1f5f9' : '#1e293b', textAlign: 'center' as const, marginBottom: 6 }}>
            {featuresHeading}
          </div>

          {/* Grid layout (default) */}
          {(!form.feature_cards_layout || form.feature_cards_layout === 'grid') && (
            <div style={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap' as const }}>
              {selectedFeatureCards.slice(0, 3).map(card => {
                const CardIcon = card.icon;
                return (
                  <div key={card.slug} style={{
                    flex: '1 1 28%', maxWidth: 80, textAlign: 'center' as const,
                    padding: 6, borderRadius: 8,
                    backgroundColor: isDark ? '#1e293b' : '#f8fafc',
                  }}>
                    <div style={{
                      width: 22, height: 22, borderRadius: 6, margin: '0 auto 4px',
                      background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <CardIcon size={11} color="#fff" strokeWidth={2.5} />
                    </div>
                    <div style={{ fontSize: 7, fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b' }}>{card.title}</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Horizontal layout */}
          {form.feature_cards_layout === 'horizontal' && (
            <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 3 }}>
              {selectedFeatureCards.slice(0, 3).map(card => {
                const CardIcon = card.icon;
                return (
                  <div key={card.slug} style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '5px 8px', borderRadius: 6,
                    border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, backgroundColor: isDark ? '#1e293b' : '#fff',
                  }}>
                    <div style={{
                      width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                      background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <CardIcon size={9} color="#fff" strokeWidth={2.5} />
                    </div>
                    <div style={{ fontSize: 7, fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b' }}>{card.title}</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Carousel layout */}
          {form.feature_cards_layout === 'carousel' && (
            <div style={{ display: 'flex', gap: 4, overflowX: 'auto' as const }}>
              {selectedFeatureCards.slice(0, 4).map(card => {
                const CardIcon = card.icon;
                return (
                  <div key={card.slug} style={{
                    flexShrink: 0, width: 56, textAlign: 'center' as const,
                    padding: '6px 4px', borderRadius: 8,
                    border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, backgroundColor: isDark ? '#1e293b' : '#fff',
                  }}>
                    <div style={{
                      width: 20, height: 20, borderRadius: 5, margin: '0 auto 3px',
                      background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <CardIcon size={10} color="#fff" strokeWidth={2.5} />
                    </div>
                    <div style={{ fontSize: 6, fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b' }}>{card.title}</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Badges layout */}
          {form.feature_cards_layout === 'badges' && (
            <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' as const, justifyContent: 'center' }}>
              {selectedFeatureCards.slice(0, 4).map(card => {
                const CardIcon = card.icon;
                return (
                  <div key={card.slug} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 3,
                    padding: '3px 8px 3px 4px', borderRadius: 99,
                    border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, backgroundColor: isDark ? '#1e293b' : '#fff',
                  }}>
                    <div style={{
                      width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                      background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <CardIcon size={8} color="#fff" strokeWidth={2.5} />
                    </div>
                    <span style={{ fontSize: 6, fontWeight: 600, color: isDark ? '#cbd5e1' : '#334155' }}>{card.title}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Info Cards */}
        {infoCards.length > 0 && (
          <div style={{ padding: '8px 12px 10px', backgroundColor: isDark ? '#1e293b' : '#f0fdfa' }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: isDark ? '#f1f5f9' : '#1e293b', textAlign: 'center' as const, marginBottom: 2 }}>
              {infoHeading}
            </div>
            <div style={{ fontSize: 7, color: isDark ? '#94a3b8' : '#64748b', textAlign: 'center' as const, marginBottom: 6 }}>
              {infoSubtitle}
            </div>

            {/* Grid layout */}
            {(!form.info_cards_layout || form.info_cards_layout === 'grid') && (
              <div style={{
                display: infoCards.length === 1 ? 'flex' : 'grid',
                ...(infoCards.length === 1
                  ? { justifyContent: 'center' }
                  : { gridTemplateColumns: infoCards.length === 2 ? '1fr 1fr' : '1fr 1fr' }),
                gap: 4,
                ...(infoCards.length <= 2 ? { maxWidth: 200, margin: '0 auto' } : {}),
              }}>
                {infoCards.slice(0, 4).map((card, i) => (
                  <div key={i} style={{
                    padding: '6px 8px', borderRadius: 6,
                    backgroundColor: isDark ? '#0f172a' : '#fff', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                    ...(infoCards.length === 1 ? { maxWidth: 120 } : {}),
                  }}>
                    <div style={{ fontSize: 7, fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b' }}>{card.title}</div>
                    {card.description && <div style={{ fontSize: 6, color: isDark ? '#94a3b8' : '#64748b', marginTop: 2 }}>{card.description}</div>}
                  </div>
                ))}
              </div>
            )}

            {/* Horizontal layout */}
            {form.info_cards_layout === 'horizontal' && (
              <div style={{
                display: 'flex', flexDirection: 'column' as const, gap: 3,
                ...(infoCards.length <= 2 ? { maxWidth: 180, margin: '0 auto' } : {}),
              }}>
                {infoCards.slice(0, 4).map((card, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    padding: '5px 7px', borderRadius: 5,
                    backgroundColor: isDark ? '#0f172a' : '#fff', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                  }}>
                    <div style={{
                      width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                      background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                    }} />
                    <div>
                      <div style={{ fontSize: 7, fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b' }}>{card.title}</div>
                      {card.description && <div style={{ fontSize: 5, color: isDark ? '#94a3b8' : '#64748b', marginTop: 1 }}>{card.description}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Carousel layout */}
            {form.info_cards_layout === 'carousel' && (
              <div style={{
                display: 'flex', gap: 4, overflowX: 'auto' as const,
                justifyContent: infoCards.length <= 2 ? 'center' : undefined,
              }}>
                {infoCards.slice(0, 6).map((card, i) => (
                  <div key={i} style={{
                    flexShrink: 0, width: 60, textAlign: 'center' as const,
                    padding: '5px 4px', borderRadius: 6,
                    backgroundColor: isDark ? '#0f172a' : '#fff', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                  }}>
                    <div style={{ fontSize: 6, fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b' }}>{card.title}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Content Hub — Tabbed Feeds */}
        {(form.show_youtube_feed || form.show_blog_feed || form.show_instagram_feed) && (
          <div style={{ padding: '10px 12px', backgroundColor: isDark ? '#0f172a' : '#f8fafc' }}>
            {/* Pill Tab Bar */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
              <div style={{
                display: 'inline-flex', gap: 3, padding: 3, borderRadius: 99,
                backgroundColor: isDark ? '#1e293b' : 'rgba(255,255,255,0.8)',
                border: `1px solid ${isDark ? '#334155' : 'rgba(226,232,240,0.6)'}`,
              }}>
                {(form.show_youtube_feed || form.show_blog_feed) && (
                  <div style={{
                    padding: '3px 8px', borderRadius: 99, fontSize: 6, fontWeight: 600,
                    background: `linear-gradient(to right, ${colors.primary}, ${colors.secondary})`,
                    color: '#fff', boxShadow: `0 2px 6px ${colors.primary}40`,
                  }}>✦ Latest</div>
                )}
                {form.show_youtube_feed && (
                  <div style={{ padding: '3px 8px', borderRadius: 99, fontSize: 6, fontWeight: 500, color: isDark ? '#94a3b8' : '#64748b' }}>▶ Videos</div>
                )}
                {form.show_blog_feed && (
                  <div style={{ padding: '3px 8px', borderRadius: 99, fontSize: 6, fontWeight: 500, color: isDark ? '#94a3b8' : '#64748b' }}>📝 Blog</div>
                )}
                {form.show_instagram_feed && (
                  <div style={{ padding: '3px 8px', borderRadius: 99, fontSize: 6, fontWeight: 500, color: isDark ? '#94a3b8' : '#64748b' }}>📷 Insta</div>
                )}
              </div>
            </div>

            {/* Mixed Feed Preview (Latest tab active) */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
              {[1,2,3].map(i => (
                <div key={i} style={{ borderRadius: 5, overflow: 'hidden', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, backgroundColor: isDark ? '#0f172a' : '#fff' }}>
                  <div style={{ aspectRatio: '16/9', backgroundColor: isDark ? '#1e293b' : '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' as const }}>
                    {i <= 2 && (
                      <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ width: 0, height: 0, borderLeft: '4px solid white', borderTop: '3px solid transparent', borderBottom: '3px solid transparent', marginLeft: 1 }} />
                      </div>
                    )}
                  </div>
                  <div style={{ padding: 4 }}>
                    <div style={{ display: 'flex', gap: 3, marginBottom: 2 }}>
                      <span style={{
                        fontSize: 4, fontWeight: 700, padding: '1px 3px', borderRadius: 99, textTransform: 'uppercase' as const,
                        backgroundColor: i <= 2 ? (isDark ? 'rgba(127,29,29,0.3)' : '#fee2e2') : (isDark ? 'rgba(30,58,138,0.3)' : '#dbeafe'),
                        color: i <= 2 ? (isDark ? '#fca5a5' : '#b91c1c') : (isDark ? '#93c5fd' : '#1d4ed8'),
                      }}>{i <= 2 ? 'VIDEO' : 'ARTICLE'}</span>
                    </div>
                    <div style={{ fontSize: 5, fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b' }}>Content title {i}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{
          padding: '10px 12px', backgroundColor: '#0f172a',
        }}>
          {/* Social */}
          <div style={{ display: 'flex', gap: 4, justifyContent: 'center', marginBottom: 6 }}>
            {socialLinks.map(link => (
              <div key={link.label} style={{
                width: 16, height: 16, borderRadius: 99,
                backgroundColor: 'rgba(255,255,255,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 6, color: '#94a3b8', fontWeight: 600,
              }}>
                {link.label.substring(0, 2)}
              </div>
            ))}
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
          }}>
            <span style={{ fontSize: 6, color: '#64748b' }}>
              &copy; {new Date().getFullYear()} {companyName}. All rights reserved.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* FONT FAMILY DROPDOWN                                                       */
/* ═══════════════════════════════════════════════════════════════════════════ */

const FONT_OPTIONS = [
  { group: 'Default', fonts: [''] },
  { group: 'Professional', fonts: GOOGLE_FONTS_PROFESSIONAL },
  { group: 'Artistic & Display', fonts: GOOGLE_FONTS_ARTISTIC },
];

function FontFamilyDropdown({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [panelStyle, setPanelStyle] = useState<{ top: number; left: number; width: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef   = useRef<HTMLDivElement>(null);
  const searchRef  = useRef<HTMLInputElement>(null);

  // Recompute the portaled panel's position from the trigger's
  // bounding rect. Called every time the dropdown opens AND while
  // open if the window resizes / scrolls — otherwise the floating
  // panel detaches from the trigger.
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setPanelStyle({
      top:   r.bottom + 4 + window.scrollY,
      left:  r.left          + window.scrollX,
      width: r.width,
    });
  }, []);

  // Close on outside click — the panel is portaled to body so we
  // also check against the panel ref (not just the trigger).
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target))   return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Position on open + listen for scroll / resize so the panel
  // sticks to the trigger as the page moves.
  useEffect(() => {
    if (!open) return;
    updatePosition();
    setTimeout(() => searchRef.current?.focus(), 50);
    const onScrollOrResize = () => updatePosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, updatePosition]);

  const filtered = search.trim()
    ? [{ group: 'Results', fonts: [...GOOGLE_FONTS_PROFESSIONAL, ...GOOGLE_FONTS_ARTISTIC].filter(f => f.toLowerCase().includes(search.toLowerCase())) }]
    : FONT_OPTIONS;

  return (
    <div className="relative">
      {/* Trigger button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(!open)}
        className="input-field w-full flex items-center justify-between text-left"
      >
        <span className={cn(value ? 'text-surface-900 dark:text-white' : 'text-surface-500 dark:text-surface-400')}>
          {value || 'Default (DM Sans)'}
        </span>
        <ChevronDown className={cn('w-4 h-4 text-surface-500 dark:text-surface-400 transition-transform', open && 'rotate-180')} />
      </button>

      {/* Dropdown panel — portaled to document.body so it escapes
          parent stacking contexts. Each Section in this form is
          wrapped in a `glass-panel` (which uses backdrop-filter and
          therefore creates its own stacking context), so a panel
          using even z-50 inside the Typography section couldn't paint
          over the sibling Logos & Icons section. createPortal lifts
          the popup out of the section's box entirely, and we position
          it via fixed + the trigger's bounding rect. */}
      {open && panelStyle && createPortal(
        <div
          ref={panelRef}
          style={{
            position: 'fixed',
            top:   panelStyle.top  - window.scrollY,
            left:  panelStyle.left - window.scrollX,
            width: panelStyle.width,
            zIndex: 9999,
          }}
          className="bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-600 rounded-lg shadow-xl overflow-hidden"
        >
          {/* Search */}
          <div className="p-2 border-b border-surface-200 dark:border-surface-700">
            <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-surface-50 dark:bg-surface-900/50 border border-surface-200 dark:border-surface-700">
              <Search className="w-3.5 h-3.5 text-surface-500 dark:text-surface-400 flex-shrink-0" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search fonts..."
                className="bg-transparent border-none outline-none text-sm text-surface-900 dark:text-surface-200 placeholder:text-surface-500 dark:placeholder:text-surface-500 w-full"
              />
            </div>
          </div>

          {/* Options list */}
          <div className="max-h-[240px] overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
            {/* Default option */}
            {!search && (
              <button
                type="button"
                onClick={() => { onChange(''); setOpen(false); setSearch(''); }}
                className={cn(
                  'w-full text-left px-3 py-2 text-sm transition-colors',
                  !value
                    ? 'badge-emerald'
                    : 'text-surface-700 dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-700'
                )}
              >
                Default (DM Sans)
              </button>
            )}
            {filtered.map(group => {
              if (group.fonts.length === 0) return null;
              if (group.group === 'Default') return null;
              return (
                <div key={group.group}>
                  <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-surface-500 dark:text-surface-400 bg-surface-50 dark:bg-surface-800/80 sticky top-0">
                    {group.group}
                  </div>
                  {group.fonts.map(f => (
                    <button
                      key={f}
                      type="button"
                      onClick={() => { onChange(f); setOpen(false); setSearch(''); }}
                      className={cn(
                        'w-full text-left px-3 py-2 text-sm transition-colors',
                        value === f
                          ? 'badge-emerald'
                          : 'text-surface-700 dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-700'
                      )}
                      style={{ fontFamily: `'${f}', sans-serif` }}
                    >
                      {f}
                    </button>
                  ))}
                </div>
              );
            })}
            {search && filtered[0]?.fonts.length === 0 && (
              <div className="px-3 py-4 text-sm text-surface-500 dark:text-surface-400 text-center">No fonts found</div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* FORM HELPERS                                                               */
/* ═══════════════════════════════════════════════════════════════════════════ */

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="glass-panel p-5">
      <h3 className="text-sm font-semibold text-surface-900 mb-4">{title}</h3>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, sub, action, children }: {
  label: string;
  sub?: string;
  /** Optional inline control next to the label (e.g. an "AI write this for me" button). */
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 mb-1">
        <label className="block text-sm font-medium text-surface-700">{label}</label>
        {action}
      </div>
      {sub && <p className="text-xs text-surface-500 mb-2">{sub}</p>}
      {children}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* SORTABLE SECTION ROW (Smart Planning tab)                                  */
/* ═══════════════════════════════════════════════════════════════════════════ */

function SortableSectionRow({
  section,
  meta,
  onToggle,
  onLabelChange,
}: {
  section: { id: string; visible: boolean; label: string };
  meta: { label: string; icon: string; locked?: boolean };
  onToggle: () => void;
  onLabelChange: (label: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.8 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-2 rounded-lg border px-3 py-2 transition-colors',
        // Theme-aware: surface-100 is light in light mode, dark in dark mode.
        // Previous values (bg-surface-800/text-surface-200) were dark-only and
        // rendered as a dark grey card on the otherwise-light Branding page.
        isDragging
          ? 'border-emerald-500 bg-surface-0 shadow-lg'
          : 'border-surface-300 bg-surface-100',
        !section.visible && !meta.locked && 'opacity-50'
      )}
    >
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-surface-500 hover:text-surface-700 touch-none"
        tabIndex={-1}
      >
        <GripVertical className="w-4 h-4" />
      </button>

      {/* Section icon & default name */}
      <span className="text-base leading-none shrink-0">{meta.icon}</span>
      <span className="text-xs font-medium text-surface-700 w-28 shrink-0 truncate">{meta.label}</span>

      {/* Custom label input */}
      <input
        className="flex-1 min-w-0 bg-transparent border-0 border-b border-surface-300 focus:border-emerald-500 text-sm text-surface-900 placeholder:text-surface-400 px-0 py-0.5 focus:ring-0 focus:outline-none"
        value={section.label}
        onChange={e => onLabelChange(e.target.value)}
        placeholder={`Custom heading (default: "${meta.label}")`}
      />

      {/* Visibility toggle */}
      <button
        onClick={onToggle}
        disabled={!!meta.locked}
        className={cn(
          'relative w-9 h-5 rounded-full transition-colors shrink-0',
          meta.locked
            ? 'bg-emerald-500/50 cursor-not-allowed'
            : section.visible ? 'bg-emerald-500' : 'bg-surface-600'
        )}
        title={meta.locked ? 'Always visible' : section.visible ? 'Click to hide' : 'Click to show'}
      >
        <span className={cn(
          'absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform shadow-sm',
          (section.visible || meta.locked) && 'translate-x-4'
        )} />
      </button>
    </div>
  );
}
